import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ViewPolicies from './ViewPolicies';
import ViewContactUs from './ViewContactUs';
import './StudentDashboard.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create bus icon
let busIconInstance = null;
const getBusIcon = () => {
  if (!busIconInstance) {
    busIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 35px;
        height: 35px;
        background-color: #FF8B3D;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
      ">🚌</div>`,
      iconSize: [35, 35],
      iconAnchor: [17, 35],
      popupAnchor: [0, -35]
    });
  }
  return busIconInstance;
};

// Create default icon for pickup locations
let defaultIconInstance = null;
const getDefaultIcon = () => {
  if (!defaultIconInstance) {
    defaultIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: white;
        font-weight: bold;
      ">📍</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }
  return defaultIconInstance;
};

function StudentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(true);
  
  // Get active tab from URL
  const getTabFromPath = (pathname) => {
    const path = pathname.split('/').pop();
    const validTabs = ['dashboard', 'schedule', 'fees', 'attendance', 'homework', 'exam-schedule', 'results', 'chat', 'transport', 'meals', 'policies', 'contact-us'];
    return validTabs.includes(path) ? path : 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  const [scheduleData, setScheduleData] = useState(null);
  const [schedulesBySemester, setSchedulesBySemester] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [schoolTimings, setSchoolTimings] = useState(null);
  const [timingsLoading, setTimingsLoading] = useState(false);
  const [studentFees, setStudentFees] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState({});
  const [homeworkData, setHomeworkData] = useState([]);
  const [homeworkLoading, setHomeworkLoading] = useState(false);
  const [homeworkDate, setHomeworkDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredHomework, setFilteredHomework] = useState([]);
  const [homeworkCompletionStatus, setHomeworkCompletionStatus] = useState({}); // {homeworkId: {is_completed, completed_at}}
  const [examScheduleData, setExamScheduleData] = useState([]);
  const [examScheduleLoading, setExamScheduleLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatGroup, setChatGroup] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const chatMessagesEndRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({}); // Track unread counts per teacher
  // Group chat state
  const [chatGroups, setChatGroups] = useState([]);
  const [selectedChatGroup, setSelectedChatGroup] = useState(null);
  const [groupChatMessages, setGroupChatMessages] = useState([]);
  const [groupChatLoading, setGroupChatLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [chatViewMode, setChatViewMode] = useState('teachers'); // 'teachers' or 'groups'
  const [examResults, setExamResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showResultCard, setShowResultCard] = useState(false);
  const [selectedResultCard, setSelectedResultCard] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // AI Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const aiChatMessagesEndRef = useRef(null);
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  
  // Transport state
  const [transportData, setTransportData] = useState(null);
  const [transportLoading, setTransportLoading] = useState(false);
  const [busLocation, setBusLocation] = useState(null);
  const [locationUpdateInterval, setLocationUpdateInterval] = useState(null);
  
  // Meals state
  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    setActiveTab(tab);
  }, [location.pathname]);

  useEffect(() => {
    // Get student data from localStorage
    const storedStudentData = localStorage.getItem('studentData');
    if (storedStudentData) {
      const parsedData = JSON.parse(storedStudentData);
      console.log('Student data:', parsedData);
      console.log('Student school_id:', parsedData.school_id);
      setStudentData(parsedData);
      
      // Check authentication status
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        console.log('Current auth session:', session ? 'Authenticated' : 'Not authenticated');
        console.log('Auth user ID:', session?.user?.id || 'None');
        if (error) {
          console.error('Auth session error:', error);
        }
      });
      
      // Fetch notices for the student's school
      if (parsedData.school_id) {
        console.log('School ID found:', parsedData.school_id);
        fetchStudentNotices(parsedData.school_id);
      } else {
        console.log('No school_id found in student data');
        setNoticesLoading(false);
      }
    } else {
      console.log('No student data found in localStorage');
      setNoticesLoading(false);
    }
    setLoading(false);
  }, []);

  // Fetch class information when studentData is loaded
  useEffect(() => {
    const fetchClassData = async () => {
      if (studentData && studentData.class_id && !studentData.classes) {
        const { data: classData, error } = await supabase
          .from('classes')
          .select('id, class_name')
          .eq('id', studentData.class_id)
          .single();

        if (!error && classData) {
          setStudentData(prev => ({
            ...prev,
            classes: classData
          }));
        }
      }
    };

    fetchClassData();
  }, [studentData?.class_id]);

  // Fetch school information when studentData is loaded - always fetch fresh from database
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (studentData && studentData.school_id) {
        // Always fetch fresh school data from Supabase to get the latest school name
        const { data: schoolData, error } = await supabase
          .from('schools')
          .select('id, school_name')
          .eq('id', studentData.school_id)
          .single();

        if (!error && schoolData) {
          setStudentData(prev => ({
            ...prev,
            schools: schoolData
          }));
          // Update localStorage with fresh school data
          const updatedStudentData = {
            ...studentData,
            schools: schoolData
          };
          localStorage.setItem('studentData', JSON.stringify(updatedStudentData));
        }
      }
    };

    fetchSchoolData();
  }, [studentData?.school_id]);

  useEffect(() => {
    if (activeTab === 'schedule' && studentData) {
      fetchStudentSchedule();
      fetchSchoolTimings();
      fetchHolidays();
    }
  }, [activeTab, studentData]);

  useEffect(() => {
    if (activeTab === 'fees' && studentData) {
      fetchStudentFees();
    }
  }, [activeTab, studentData]);

  useEffect(() => {
    if (activeTab === 'attendance' && studentData) {
      fetchStudentAttendance();
    }
  }, [activeTab, studentData, selectedMonth, selectedYear]);

  useEffect(() => {
    if (activeTab === 'homework' && studentData) {
      fetchStudentHomework();
    }
  }, [activeTab, studentData]);

  // Fetch transport data when transport tab is active
  useEffect(() => {
    if (activeTab === 'transport' && studentData) {
      fetchTransportData();
    }
  }, [activeTab, studentData]);

  // Fetch meals when meals tab is active
  useEffect(() => {
    if (activeTab === 'meals' && studentData) {
      fetchMeals();
    }
  }, [activeTab, studentData]);

  // Set up location update interval when transport data is available
  useEffect(() => {
    if (activeTab === 'transport' && transportData) {
      // Initial location update
      updateBusLocation();
      
      // Set up interval to update bus location every 10 seconds
      const interval = setInterval(() => {
        updateBusLocation();
      }, 10000);
      setLocationUpdateInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (locationUpdateInterval) {
        clearInterval(locationUpdateInterval);
        setLocationUpdateInterval(null);
      }
    }
  }, [activeTab, transportData]);

  useEffect(() => {
    if (activeTab === 'exam-schedule' && studentData) {
      fetchExamSchedule();
    }
  }, [activeTab, studentData]);

  useEffect(() => {
    if (activeTab === 'chat' && studentData) {
      fetchStudentTeachers();
      fetchStudentChatGroups();
    }
  }, [activeTab, studentData]);

  useEffect(() => {
    if (activeTab === 'chat' && selectedTeacher && studentData && !chatGroup) {
      loadOrCreateChatGroup();
    } else if (activeTab === 'chat' && !selectedTeacher) {
      setChatMessages([]);
      setChatGroup(null);
      // Clean up real-time subscription
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
      // Clean up group chat channel
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }
    }

    // Cleanup on unmount or tab change
    return () => {
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }
    };
  }, [selectedTeacher, activeTab, studentData]);

  // Separate effect for polling messages (only when chat group exists)
  useEffect(() => {
    let messagePollInterval = null;

    if (activeTab === 'chat' && chatGroup && chatGroup.id && !chatLoading) {
      // Fallback: Poll for new messages every 3 seconds if real-time doesn't work
      messagePollInterval = setInterval(async () => {
        const { data: latestMessages } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('group_id', chatGroup.id)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (latestMessages && latestMessages.length > 0) {
          const latestMessage = latestMessages[0];
          setChatMessages(prev => {
            const exists = prev.some(m => m.id === latestMessage.id);
            if (!exists) {
              return [...prev, latestMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            }
            return prev;
          });
        }
      }, 3000);
    }

    return () => {
      if (messagePollInterval) {
        clearInterval(messagePollInterval);
      }
    };
  }, [chatGroup, activeTab, chatLoading]);

  // Scroll to bottom when chat messages change and mark as read
  useEffect(() => {
    if (chatMessagesEndRef.current && selectedTeacher && studentData) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      
      // Mark messages as read when user views the chat
      if (chatMessages && chatMessages.length > 0) {
        const lastMessage = chatMessages[chatMessages.length - 1];
        const lastReadKey = `lastRead_${studentData.id}_${selectedTeacher.id}`;
        localStorage.setItem(lastReadKey, lastMessage.created_at);
        
        // Update unread count to 0
        setUnreadCounts(prev => ({
          ...prev,
          [selectedTeacher.id]: 0
        }));
      }
    }
  }, [chatMessages, selectedTeacher, studentData]);

  useEffect(() => {
    if (activeTab === 'results' && studentData) {
      fetchExamResults();
    }
  }, [activeTab, studentData]);

  const fetchStudentNotices = async (schoolId) => {
    try {
      setNoticesLoading(true);
      console.log('=== FETCHING NOTICES ===');
      console.log('School ID:', schoolId);
      
      // First, verify authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Auth session exists:', !!session);
      console.log('Auth user ID:', session?.user?.id || 'NONE');
      
      if (sessionError) {
        console.error('Session error:', sessionError);
      }
      
      // Get current date for filtering expired notices
      const today = new Date().toISOString().split('T')[0];
      
      // Try a simple query first to test RLS
      console.log('Testing simple query without filters...');
      const testQuery = await supabase
        .from('notices')
        .select('id, school_id, target_audience, is_active')
        .limit(5);
      
      console.log('Test query result:', testQuery.data);
      console.log('Test query error:', testQuery.error);
      
      // Build query to fetch notices
      // Filter by: school_id, target_audience='students', is_active=true
      // We'll filter by status and expiry_date in JavaScript
      console.log('Fetching notices with filters...');
      let { data, error } = await supabase
        .from('notices')
        .select('*')
        .eq('school_id', schoolId)
        .eq('target_audience', 'students')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching notices with school_id:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        // Check if it's an RLS policy error
        if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('policy')) {
          console.error('⚠️ RLS POLICY ERROR: Students may not have permission to read notices');
          console.error('The "Allow authenticated users to read notices" policy should allow this.');
          console.error('Checking if student auth_user_id matches...');
          
          // Check student record
          if (session?.user?.id) {
            const { data: studentCheck, error: studentCheckError } = await supabase
              .from('students')
              .select('id, auth_user_id, school_id, email')
              .eq('auth_user_id', session.user.id)
              .single();
            
            console.log('Student record check:', studentCheck);
            console.log('Student check error:', studentCheckError);
            
            if (studentCheck) {
              console.log('✅ Student found with auth_user_id:', studentCheck.auth_user_id);
              console.log('Student school_id:', studentCheck.school_id);
              console.log('Notice school_id needed:', schoolId);
            } else {
              console.error('❌ No student record found with auth_user_id:', session.user.id);
            }
          }
        }
        
        // Fallback 1: try to fetch all student notices without school_id filter
        console.log('🔄 Fallback 1: Fetching all student notices (no school_id filter)...');
        const fallbackResult = await supabase
          .from('notices')
          .select('*')
          .eq('target_audience', 'students')
          .eq('is_active', true)
          .order('created_at', { ascending: false });
        
        if (fallbackResult.error) {
          console.error('❌ Fallback 1 error:', fallbackResult.error);
          
          // Fallback 2: try with no filters at all
          console.log('🔄 Fallback 2: Fetching all notices (no filters)...');
          const fallback2Result = await supabase
            .from('notices')
            .select('*')
            .limit(10);
          
          if (fallback2Result.error) {
            console.error('❌ Fallback 2 error:', fallback2Result.error);
            setNotices([]);
            return;
          }
          
          // Filter in JavaScript
          if (fallback2Result.data && Array.isArray(fallback2Result.data)) {
            data = fallback2Result.data.filter(notice => 
              notice.school_id === schoolId && 
              notice.target_audience === 'students' && 
              notice.is_active === true
            );
            console.log('✅ Fallback 2: Filtered notices in JavaScript:', data.length);
          } else {
            data = [];
          }
        } else {
          // Filter by school_id in JavaScript as fallback
          if (fallbackResult.data && Array.isArray(fallbackResult.data)) {
            data = fallbackResult.data.filter(notice => notice.school_id === schoolId);
            console.log('✅ Fallback 1: Filtered notices by school_id in JavaScript:', data.length);
          } else {
            data = fallbackResult.data;
          }
        }
      } else {
        console.log('✅ Query successful! Found', data?.length || 0, 'notices');
      }

      // Log raw data before filtering
      console.log('Raw notices from database:', data);
      console.log('Total notices before filtering:', data?.length || 0);
      console.log('Query parameters - school_id:', schoolId, 'target_audience: students, is_active: true');

      // Filter out expired notices (status column doesn't exist in database)
      if (data && Array.isArray(data)) {
        const beforeFilterCount = data.length;
        data = data.filter(notice => {
          // Filter out expired notices only
          if (notice.expiry_date) {
            const expiryDate = new Date(notice.expiry_date);
            const todayDate = new Date(today);
            if (expiryDate < todayDate) {
              console.log(`Filtering out notice ${notice.id}: expired on ${notice.expiry_date}`);
              return false; // Notice has expired
            }
          }
          
          return true;
        });
        console.log(`Filtered ${beforeFilterCount} notices down to ${data.length} valid notices`);
      }

      console.log('Final notices to display:', data);
      console.log('Number of notices found:', data?.length || 0);
      setNotices(data || []);
    } catch (err) {
      console.error('Error fetching notices:', err);
      setNotices([]);
    } finally {
      setNoticesLoading(false);
    }
  };

  const fetchStudentSchedule = async () => {
    if (!studentData) return;
    
    try {
      setScheduleLoading(true);
      console.log('Fetching schedule for student:', {
        school_id: studentData.school_id,
        class_id: studentData.class_id,
        section_id: studentData.section_id,
        full_student_data: studentData
      });

      // DEBUG: Let's check what's in the database first
      console.log('=== CHECKING ALL SCHEDULES IN DATABASE ===');
      
      // First, let's check if we can access the table at all
      console.log('Testing basic table access...');
      const { data: testAccess, error: testError } = await supabase
        .from('class_schedules')
        .select('count(*)');
      
      console.log('Test access result:', testAccess, 'Error:', testError);
      
      // Now try to get all schedules
      const { data: allSchedulesDebug, error: allSchedulesDebugError } = await supabase
        .from('class_schedules')
        .select('*');
      
      console.log('All schedules in database:', allSchedulesDebug);
      console.log('All schedules error:', allSchedulesDebugError);
      console.log('Total schedules:', allSchedulesDebug?.length || 0);
      
      // Let's also check the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Current user:', user);
      console.log('User error:', userError);
      
      if (allSchedulesDebug && allSchedulesDebug.length > 0) {
        console.log('Sample schedule:', allSchedulesDebug[0]);
        console.log('Unique school_ids:', [...new Set(allSchedulesDebug.map(s => s.school_id))]);
        console.log('Unique class_ids:', [...new Set(allSchedulesDebug.map(s => s.class_id))]);
        console.log('Unique section_names:', [...new Set(allSchedulesDebug.map(s => s.section_name))]);
      }

      // Debug: Show what we're looking for vs what's in the database
      if (allSchedulesDebug && allSchedulesDebug.length > 0) {
        console.log('=== DEBUGGING SCHEDULE MATCH ===');
        console.log('Looking for:', {
          school_id: studentData.school_id,
          class_id: studentData.class_id,
          section_name: studentData.section_id
        });
        console.log('Found in database:', allSchedulesDebug.map(s => ({
          school_id: s.school_id,
          class_id: s.class_id,
          section_name: s.section_name,
          day: s.day,
          period: s.period,
          subject_name: s.subject_name
        })));
        console.log('=== END DEBUGGING ===');
      }

      // Fetch ALL schedules for this student's class and section (all semesters)
      const { data: allSchedulesData, error: schedulesError } = await supabase
        .from('class_schedules')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('section_name', studentData.section_id)
        .order('day')
        .order('period');

      console.log('All schedules query result:', allSchedulesData, 'Error:', schedulesError);

      if (schedulesError) {
        console.error('Error fetching schedules:', schedulesError);
        setScheduleData(null);
        setSchedulesBySemester({});
        return;
      }

      if (allSchedulesData && allSchedulesData.length > 0) {
        // Group schedules by semester
        const schedulesGrouped = {};
        
        allSchedulesData.forEach(entry => {
          const semester = entry.semester || '1st Semester';
          const semesterName = entry.semester_name || semester;
          
          if (!schedulesGrouped[semester]) {
            schedulesGrouped[semester] = {
              semester: semester,
              semester_name: semesterName,
              schedule: {},
              created_at: entry.created_at,
              updated_at: entry.updated_at
            };
          }
          
          const key = `${entry.day}_${entry.period}`;
          schedulesGrouped[semester].schedule[key] = {
            subject_name: entry.subject_name,
            teacher_name: entry.teacher_name,
            teacher_id: entry.teacher_id,
            subject_id: entry.subject_id
          };
          
          // Update timestamps if this entry is newer
          if (entry.updated_at && (!schedulesGrouped[semester].updated_at || entry.updated_at > schedulesGrouped[semester].updated_at)) {
            schedulesGrouped[semester].updated_at = entry.updated_at;
          }
        });

        // Sort semesters: "1st Semester" first, then others alphabetically
        const sortedSemesters = Object.keys(schedulesGrouped).sort((a, b) => {
          if (a === '1st Semester') return -1;
          if (b === '1st Semester') return 1;
          return a.localeCompare(b);
        });

        const orderedSchedules = {};
        sortedSemesters.forEach(sem => {
          orderedSchedules[sem] = schedulesGrouped[sem];
        });

        setSchedulesBySemester(orderedSchedules);
        
        // Set the first semester as default scheduleData for backward compatibility
        const firstSemester = sortedSemesters[0];
        if (firstSemester) {
          const firstScheduleData = {
            school_id: studentData.school_id,
            class_id: studentData.class_id,
            class_name: studentData.classes?.class_name || 'Unknown Class',
            section_id: studentData.section_id,
            section_name: studentData.section_id || 'Unknown Section',
            schedule: orderedSchedules[firstSemester].schedule,
            created_at: orderedSchedules[firstSemester].created_at,
            updated_at: orderedSchedules[firstSemester].updated_at,
            semester: firstSemester,
            semester_name: orderedSchedules[firstSemester].semester_name
          };
          setScheduleData(firstScheduleData);
        }

        console.log('✅ Loaded schedule data from Supabase:', orderedSchedules);
      } else {
        console.log('No schedule found in database');
        setScheduleData(null);
        setSchedulesBySemester({});
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
      setScheduleData(null);
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchSchoolTimings = async () => {
    if (!studentData) return;
    
    try {
      setTimingsLoading(true);
      console.log('Fetching school timings for school:', studentData.school_id);
      
      const { data, error } = await supabase
        .from('school_timings')
        .select('*')
        .eq('school_id', studentData.school_id)
        .is('class_id', null) // School-wide timings
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) {
        console.error('Error fetching school timings:', error);
        return;
      }
      
      if (data && data.length > 0) {
        setSchoolTimings(data[0]);
        console.log('Found school timings:', data[0]);
        console.log('Start time:', data[0].start_time, 'Type:', typeof data[0].start_time);
        console.log('End time:', data[0].end_time, 'Type:', typeof data[0].end_time);
      } else {
        console.log('No school timings found');
        setSchoolTimings(null);
      }
    } catch (err) {
      console.error('Error fetching school timings:', err);
      setSchoolTimings(null);
    } finally {
      setTimingsLoading(false);
    }
  };

  const fetchHolidays = async () => {
    if (!studentData) return;
    
    try {
      console.log('=== FETCHING HOLIDAYS FOR STUDENT ===');
      console.log('School ID:', studentData.school_id);
      
      const { data, error } = await supabase
        .from('schedules')
        .select('holidays')
        .eq('school_id', studentData.school_id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching holidays:', error);
        return;
      }

      console.log('Raw holiday data from database:', data);

      // Set holidays from the most recent schedule, or empty object if none found
      if (data && data.length > 0 && data[0].holidays) {
        setHolidays(data[0].holidays);
        console.log('Loaded holidays for student:', data[0].holidays);
      } else {
        setHolidays({});
        console.log('No holidays found for student, using default working days');
      }
      
      console.log('=== END FETCHING HOLIDAYS FOR STUDENT ===');
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setHolidays({});
    }
  };

  const fetchStudentFees = async () => {
    if (!studentData) return;
    
    try {
      setFeesLoading(true);
      console.log('=== FETCHING STUDENT FEES ===');
      console.log('Student data:', studentData);
      console.log('Student ID:', studentData.id);
      console.log('Student School ID:', studentData.school_id);
      console.log('Student Class ID:', studentData.class_id);
      console.log('Student Class Name:', studentData.classes?.class_name);
      
      // Get today's date
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      console.log('Today\'s date:', todayStr);
      
      // First, let's check if class_fees table has any data for this class
      console.log('Checking class_fees table...');
      const { data: allClassFees, error: allClassFeesError } = await supabase
        .from('class_fees')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('is_active', true);

      console.log('All class fees for school:', allClassFees, 'Error:', allClassFeesError);
      
      // Fetch class fees for default amounts
      const { data: classFeesData, error: classFeesError } = await supabase
        .from('class_fees')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('is_active', true)
        .single();

      console.log('Class fees for student\'s class:', classFeesData, 'Error:', classFeesError);

      // Fetch fee schedule for the student's class
      const { data: feeScheduleData, error: scheduleError } = await supabase
        .from('fee_schedule')
        .select(`
          *,
          classes(class_name)
        `)
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('is_active', true)
        .order('fee_date', { ascending: true });

      console.log('Fee schedule data:', feeScheduleData, 'Error:', scheduleError);

      // If no class fees found, let's try to find any fees for this school
      if (!classFeesData && classFeesError?.code === 'PGRST116') {
        console.log('No class fees found for this specific class. Checking all fees for this school...');
        
        const { data: schoolFees, error: schoolFeesError } = await supabase
          .from('class_fees')
          .select('*')
          .eq('school_id', studentData.school_id)
          .eq('is_active', true);

        console.log('All school fees:', schoolFees, 'Error:', schoolFeesError);
        
        if (schoolFees && schoolFees.length > 0) {
          console.log('Found fees for other classes in the school. Student might be in a different class.');
          // For now, let's use the first available fee as a fallback
          const fallbackFee = schoolFees[0];
          console.log('Using fallback fee:', fallbackFee);
          
          // Fetch additional fees for the fallback class
          const { data: fallbackAdditionalFees, error: fallbackAdditionalError } = await supabase
            .from('additional_class_fees')
            .select('*')
            .eq('school_id', studentData.school_id)
            .eq('class_id', fallbackFee.class_id)
            .eq('is_active', true);
          
          // Calculate additional fees
          const monthlyAdditionalFees = fallbackAdditionalFees
            ?.filter(fee => fee.period === 'monthly')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
          
          const oneTimeAdditionalFees = fallbackAdditionalFees
            ?.filter(fee => fee.period === 'one_time')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
          
          const totalFeeAmount = fallbackFee.amount + monthlyAdditionalFees + oneTimeAdditionalFees;
          
          // Generate a simple fee display
          const monthlyFees = [{
            month: 'Current Month',
            periodStart: new Date(),
            periodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
            dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000), // 15 days from now
            feeAmount: totalFeeAmount,
            baseFeeAmount: fallbackFee.amount,
            additionalFees: monthlyAdditionalFees + oneTimeAdditionalFees,
            currency: fallbackFee.currency,
            feeType: fallbackFee.fee_type,
            isScheduled: false,
            description: fallbackFee.description || '',
            isDueNow: true
          }];
          
          console.log('Generated fallback fees:', monthlyFees);
          setStudentFees(monthlyFees);
          return;
        }
      }

      // If we have class fees data, proceed with normal logic
      if (classFeesData) {
        console.log('Proceeding with normal fee calculation...');
        
        // Fetch additional fees for the student's class
        console.log('Fetching additional fees...');
        const { data: additionalFeesData, error: additionalFeesError } = await supabase
          .from('additional_class_fees')
          .select('*')
          .eq('school_id', studentData.school_id)
          .eq('class_id', studentData.class_id)
          .eq('is_active', true);
        
        console.log('Additional fees data:', additionalFeesData, 'Error:', additionalFeesError);
        
        // Calculate additional fees
        const monthlyAdditionalFees = additionalFeesData
          ?.filter(fee => fee.period === 'monthly')
          .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
        
        const oneTimeAdditionalFees = additionalFeesData
          ?.filter(fee => fee.period === 'one_time')
          .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
        
        console.log('Monthly additional fees:', monthlyAdditionalFees);
        console.log('One-time additional fees:', oneTimeAdditionalFees);
        
        // Fetch the actual academic year from database
        console.log('Fetching academic year from database...');
        const { data: academicYearData, error: academicYearError } = await supabase
          .from('academic_years')
          .select('*')
          .eq('school_id', studentData.school_id)
          .eq('is_active', true)
          .single();

        let academicYearStart, academicYearEnd;
        
        if (academicYearData && !academicYearError) {
          console.log('Found academic year in database:', academicYearData);
          academicYearStart = new Date(academicYearData.start_date);
          academicYearEnd = new Date(academicYearData.end_date);
        } else {
          console.log('No academic year found in database, using fallback dates');
          // Fallback: Use the first_fee_date from class fees to determine academic year
          if (classFeesData.first_fee_date) {
            const firstFeeDate = new Date(classFeesData.first_fee_date);
            academicYearStart = new Date(firstFeeDate.getFullYear(), firstFeeDate.getMonth(), 1);
            academicYearEnd = new Date(firstFeeDate.getFullYear() + 1, firstFeeDate.getMonth() - 1, 0);
          } else {
            // Last resort: Use current date-based calculation
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();
            
            if (currentMonth >= 7) {
              academicYearStart = new Date(currentYear, 7, 1);
              academicYearEnd = new Date(currentYear + 1, 6, 31);
            } else {
              academicYearStart = new Date(currentYear - 1, 7, 1);
              academicYearEnd = new Date(currentYear, 6, 31);
            }
          }
        }
        
        console.log('Using academic year:', academicYearStart.getFullYear() + '-' + academicYearEnd.getFullYear());
        console.log('Academic year start:', academicYearStart.toLocaleDateString());
        console.log('Academic year end:', academicYearEnd.toLocaleDateString());
        
        const monthlyFees = [];
        let currentDate = new Date(academicYearStart);
        let isFirstMonth = true; // Track if this is the first month to apply one-time fees
        
        while (currentDate <= academicYearEnd) {
          const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          
          // Check if there's a scheduled fee for this month
          const scheduledFee = feeScheduleData?.find(fee => {
            const feeDate = new Date(fee.fee_date);
            return feeDate.getMonth() === currentDate.getMonth() && 
                   feeDate.getFullYear() === currentDate.getFullYear();
          });

          // Calculate period and due date based on class fees
          let periodStart = monthStart;
          let periodEnd = monthEnd;
          let dueDate = monthStart;
          
          if (classFeesData.first_fee_date) {
            const firstFeeDate = new Date(classFeesData.first_fee_date);
            const expiryDuration = classFeesData.expiry_duration || 30;
            
            if (currentDate.getMonth() === firstFeeDate.getMonth() && 
                currentDate.getFullYear() === firstFeeDate.getFullYear()) {
              // First month
              periodStart = new Date(firstFeeDate);
              periodEnd = new Date(firstFeeDate);
              periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              dueDate = new Date(firstFeeDate);
              dueDate.setDate(dueDate.getDate() + expiryDuration);
            } else {
              // Subsequent months
              const monthsFromStart = (currentDate.getFullYear() - firstFeeDate.getFullYear()) * 12 + 
                                     (currentDate.getMonth() - firstFeeDate.getMonth());
              
              switch (classFeesData.fee_type) {
                case 'monthly':
                  periodStart = new Date(firstFeeDate);
                  periodStart.setMonth(periodStart.getMonth() + monthsFromStart);
                  periodEnd = new Date(periodStart);
                  periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                  dueDate = new Date(periodStart);
                  dueDate.setDate(dueDate.getDate() + expiryDuration);
                  break;
                case 'quarterly':
                  periodStart = new Date(firstFeeDate);
                  periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 3));
                  periodEnd = new Date(periodStart);
                  periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                  dueDate = new Date(periodStart);
                  dueDate.setDate(dueDate.getDate() + expiryDuration);
                  break;
                case 'semester':
                  periodStart = new Date(firstFeeDate);
                  periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 6));
                  periodEnd = new Date(periodStart);
                  periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                  dueDate = new Date(periodStart);
                  dueDate.setDate(dueDate.getDate() + expiryDuration);
                  break;
                case 'annual':
                  if (monthsFromStart === 0) {
                    periodStart = new Date(firstFeeDate);
                    periodEnd = new Date(firstFeeDate);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    dueDate = new Date(firstFeeDate);
                    dueDate.setDate(dueDate.getDate() + expiryDuration);
                  } else {
                    currentDate.setMonth(currentDate.getMonth() + 1);
                    continue;
                  }
                  break;
              }
            }
          }

          // Use scheduled fee data if available, otherwise use class fee defaults
          const baseFeeAmount = scheduledFee ? scheduledFee.amount : classFeesData.amount;
          
          // Add additional fees to the base amount
          // One-time fees only apply to the first month
          const oneTimeFee = isFirstMonth ? oneTimeAdditionalFees : 0;
          const finalFeeAmount = baseFeeAmount + monthlyAdditionalFees + oneTimeFee;
          
          const finalCurrency = scheduledFee ? scheduledFee.currency : classFeesData.currency || 'PKR';
          const finalFeeType = scheduledFee ? scheduledFee.fee_type : classFeesData.fee_type || 'monthly';
          
          // Due date should always be periodStart + expiryDuration (not the scheduled fee date)
          // The expiry duration comes from class fees
          const expiryDuration = classFeesData.expiry_duration || 30;
          const finalDueDate = new Date(periodStart);
          finalDueDate.setDate(finalDueDate.getDate() + expiryDuration);

          // Check if this fee is due now (today falls within the period)
          const periodStartStr = periodStart.toISOString().split('T')[0];
          const periodEndStr = periodEnd.toISOString().split('T')[0];
          const dueDateStr = finalDueDate.toISOString().split('T')[0];
          
          console.log(`Checking month ${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}:`);
          console.log(`  Period: ${periodStartStr} to ${periodEndStr}`);
          console.log(`  Due Date: ${dueDateStr}`);
          console.log(`  Today: ${todayStr}`);
          console.log(`  Is due now: ${todayStr >= periodStartStr && todayStr <= periodEndStr}`);
          
          // Show fee if today falls within the period (not just up to due date)
          if (todayStr >= periodStartStr && todayStr <= periodEndStr) {
            monthlyFees.push({
              month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
              periodStart: periodStart,
              periodEnd: periodEnd,
              dueDate: finalDueDate,
              feeAmount: finalFeeAmount,
              baseFeeAmount: baseFeeAmount,
              additionalFees: monthlyAdditionalFees + oneTimeFee,
              currency: finalCurrency,
              feeType: finalFeeType,
              isScheduled: !!scheduledFee,
              description: scheduledFee?.description || '',
              isDueNow: true
            });
          }

          // Mark that we've processed the first month
          if (isFirstMonth) {
            isFirstMonth = false;
          }

          currentDate.setMonth(currentDate.getMonth() + 1);
        }

        console.log('Generated monthly fees:', monthlyFees);
        
        // Fetch payment status from fee_records table
        if (monthlyFees.length > 0) {
          console.log('Fetching payment status from fee_records...');
          const feePeriods = monthlyFees.map(fee => {
            // Convert fee month to YYYY-MM format (e.g., "December 2025" -> "2025-12")
            const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
            const monthName = monthParts[0] || '';
            const year = monthParts[1] || new Date().getFullYear().toString();
            const monthMap = {
              'January': '01', 'February': '02', 'March': '03', 'April': '04',
              'May': '05', 'June': '06', 'July': '07', 'August': '08',
              'September': '09', 'October': '10', 'November': '11', 'December': '12'
            };
            const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
            return `${year}-${monthNum}`;
          });
          
          console.log('Fee periods to check:', feePeriods);
          
          const { data: feeRecordsData, error: feeRecordsError } = await supabase
            .from('fee_records')
            .select('*')
            .eq('school_id', studentData.school_id)
            .eq('class_id', studentData.class_id)
            .eq('section_id', studentData.section_id)
            .eq('student_id', studentData.id)
            .in('fee_period', feePeriods);
          
          console.log('Fee records data:', feeRecordsData, 'Error:', feeRecordsError);
          
          // Create a map of fee_period -> is_paid
          const paymentStatusMap = {};
          if (feeRecordsData) {
            feeRecordsData.forEach(record => {
              paymentStatusMap[record.fee_period] = record.is_paid;
            });
          }
          
          console.log('Payment status map:', paymentStatusMap);
          
          // Update monthlyFees with payment status
          monthlyFees.forEach(fee => {
            const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
            const monthName = monthParts[0] || '';
            const year = monthParts[1] || new Date().getFullYear().toString();
            const monthMap = {
              'January': '01', 'February': '02', 'March': '03', 'April': '04',
              'May': '05', 'June': '06', 'July': '07', 'August': '08',
              'September': '09', 'October': '10', 'November': '11', 'December': '12'
            };
            const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
            const feePeriod = `${year}-${monthNum}`;
            fee.isPaid = paymentStatusMap[feePeriod] || false;
            console.log(`Fee for ${fee.month} (${feePeriod}): isPaid = ${fee.isPaid}`);
          });
        }
        
        setStudentFees(monthlyFees);
      } else {
        console.log('No class fees data found, setting empty array');
        setStudentFees([]);
      }
    } catch (err) {
      console.error('Error fetching student fees:', err);
      setStudentFees([]);
    } finally {
      setFeesLoading(false);
    }
  };

  const fetchStudentAttendance = async () => {
    if (!studentData) return;
    
    try {
      setAttendanceLoading(true);
      console.log('=== FETCHING STUDENT ATTENDANCE ===');
      console.log('Student data:', studentData);
      console.log('Student ID:', studentData.id);
      console.log('Student School ID:', studentData.school_id);
      console.log('Student Class ID:', studentData.class_id);
      console.log('Student Section ID:', studentData.section_id);
      console.log('Selected Month:', selectedMonth);
      console.log('Selected Year:', selectedYear);

      // Calculate date range for the selected month
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];

      console.log('Date range:', { startDate, endDate });

      // Fetch attendance records for this student
      const { data: records, error: recordsError } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('section_id', studentData.section_id)
        .eq('student_id', studentData.id)
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate)
        .order('attendance_date', { ascending: false });

      if (recordsError) {
        console.error('Error fetching attendance records:', recordsError);
        return;
      }

      console.log('Attendance records fetched:', records);

      // Fetch teacher data for the records
      const teacherIds = [...new Set(records.map(record => record.teacher_id))];
      let teachers = [];
      
      if (teacherIds.length > 0) {
        const { data: teachersData, error: teachersError } = await supabase
          .from('teachers')
          .select('*')
          .eq('school_id', studentData.school_id)
          .in('id', teacherIds);

        if (teachersError) {
          console.error('Error fetching teachers:', teachersError);
        } else {
          teachers = teachersData || [];
        }
      }

      // Create teacher map
      const teachersMap = {};
      teachers.forEach(teacher => {
        teachersMap[teacher.id] = teacher;
      });

      // Enrich records with teacher data
      const enrichedRecords = records.map(record => ({
        ...record,
        teacher: teachersMap[record.teacher_id]
      }));

      console.log('Enriched attendance records:', enrichedRecords);
      setAttendanceRecords(enrichedRecords);

    } catch (error) {
      console.error('Error fetching student attendance:', error);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchStudentHomework = async () => {
    if (!studentData) return;
    
    try {
      setHomeworkLoading(true);
      console.log('=== FETCHING STUDENT HOMEWORK ===');
      console.log('Student data:', studentData);
      console.log('Student Class ID:', studentData.class_id);
      console.log('Student Section ID:', studentData.section_id);

      // Fetch ALL homework for this student's class and section (not just today's)
      const { data: homework, error: homeworkError } = await supabase
        .from('homework')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('section_name', studentData.section_id)
        .order('assigned_date', { ascending: false });

      if (homeworkError) {
        console.error('Error fetching homework:', homeworkError);
        return;
      }

      console.log('Homework data fetched:', homework);
      setHomeworkData(homework || []);
      
      // Fetch completion status for all homework
      if (homework && homework.length > 0 && studentData.id) {
        const homeworkIds = homework.map(h => h.id);
        
        const { data: completionData, error: completionError } = await supabase
          .from('homework_completion')
          .select('*')
          .eq('student_id', studentData.id)
          .in('homework_id', homeworkIds);

        if (!completionError && completionData) {
          const statusMap = {};
          completionData.forEach(completion => {
            statusMap[completion.homework_id] = {
              is_completed: completion.is_completed,
              completed_at: completion.completed_at
            };
          });
          setHomeworkCompletionStatus(statusMap);
        }
      }
      
      // Filter homework for the selected date
      filterHomeworkByDate(homework || [], homeworkDate);

    } catch (error) {
      console.error('Error fetching student homework:', error);
    } finally {
      setHomeworkLoading(false);
    }
  };

  const fetchExamSchedule = async () => {
    if (!studentData) return;
    
    try {
      setExamScheduleLoading(true);
      console.log('=== FETCHING EXAM SCHEDULE ===');
      console.log('Student data:', studentData);
      console.log('Student Class ID:', studentData.class_id);
      console.log('Student Section ID:', studentData.section_id);

      // Fetch exams for this student's class
      // If section is specified, show exams for that section or class-wide exams
      // If no section is specified, show all exams for the class
      let query = supabase
        .from('exam_schedule')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('class_id', studentData.class_id)
        .eq('is_active', true)
        .order('start_date', { ascending: true });

      const { data: exams, error: examError } = await query;

      if (examError) {
        console.error('Error fetching exam schedule:', examError);
        return;
      }

      console.log('Exam schedule data fetched:', exams);
      
      // Filter exams based on section
      let filteredExams = exams || [];
      if (studentData.section_id) {
        // Show exams for this specific section OR class-wide exams (where section_name is null)
        filteredExams = exams.filter(exam => 
          !exam.section_name || exam.section_name === studentData.section_id
        );
      }
      // If no section_id, show all exams for the class

      console.log('Filtered exam schedule:', filteredExams);
      setExamScheduleData(filteredExams);

    } catch (error) {
      console.error('Error fetching exam schedule:', error);
    } finally {
      setExamScheduleLoading(false);
    }
  };

  const fetchExamResults = async () => {
    if (!studentData) return;
    
    try {
      setResultsLoading(true);
      
      // Fetch published exam marks for the student
      const { data: marksData, error: marksError } = await supabase
        .from('exam_marks')
        .select(`
          id,
          exam_schedule_id,
          student_id,
          subject_name,
          obtained_marks,
          total_marks,
          grade,
          percentage,
          remarks,
          created_at
        `)
        .eq('student_id', studentData.id)
        .eq('is_published', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (marksError) throw marksError;

      // Fetch exam schedule details
      const examIds = [...new Set(marksData.map(m => m.exam_schedule_id))];
      let examsData = [];
      
      if (examIds.length > 0) {
        const { data: exams, error: examsError } = await supabase
          .from('exam_schedule')
          .select('id, title, type, start_date, end_date, is_published')
          .in('id', examIds)
          .eq('is_published', true)
          .eq('is_active', true);

        if (examsError) throw examsError;
        examsData = exams || [];
      }

      // Fetch class information
      let classData = null;
      if (studentData.class_id) {
        const { data: classes, error: classesError } = await supabase
          .from('classes')
          .select('id, class_name')
          .eq('id', studentData.class_id)
          .single();

        if (!classesError) {
          classData = classes;
        }
      }

      // Group marks by exam
      const resultsByExam = {};
      
      marksData.forEach(mark => {
        const exam = examsData.find(e => e.id === mark.exam_schedule_id);
        const examId = mark.exam_schedule_id;
        
        if (!resultsByExam[examId]) {
          resultsByExam[examId] = {
            exam: exam || null,
            marks: []
          };
        }
        
        resultsByExam[examId].marks.push(mark);
      });

      // Convert to array format
      const formattedResults = [{
        student: {
          ...studentData,
          classes: classData || null
        },
        examGroups: Object.values(resultsByExam)
      }];

      setExamResults(formattedResults);
    } catch (error) {
      console.error('Error fetching exam results:', error);
      setExamResults([]);
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchMeals = async () => {
    if (!studentData?.school_id) return;
    
    try {
      setMealsLoading(true);
      const { data, error } = await supabase
        .from('meals')
        .select('*')
        .eq('school_id', studentData.school_id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching meals:', error);
        setMeals([]);
      } else {
        setMeals(data || []);
      }
    } catch (err) {
      console.error('Error fetching meals:', err);
      setMeals([]);
    } finally {
      setMealsLoading(false);
    }
  };

  const filterHomeworkByDate = (homework, selectedDate) => {
    const filtered = homework.filter(hw => hw.assigned_date === selectedDate);
    setFilteredHomework(filtered);
    console.log(`Filtered homework for ${selectedDate}:`, filtered);
  };

  const navigateHomeworkDate = (direction) => {
    const currentDate = new Date(homeworkDate);
    
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    const newDate = currentDate.toISOString().split('T')[0];
    setHomeworkDate(newDate);
    filterHomeworkByDate(homeworkData, newDate);
  };

  const getAvailableDates = () => {
    if (!homeworkData.length) return [];
    
    const dates = [...new Set(homeworkData.map(hw => hw.assigned_date))];
    return dates.sort((a, b) => new Date(b) - new Date(a)); // Most recent first
  };

  const handleLogout = () => {
    localStorage.removeItem('studentData');
    localStorage.removeItem('userType');
    navigate('/');
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'normal': return '🟢';
      default: return '🟢';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'High Priority';
      case 'medium': return 'Medium Priority';
      case 'normal': return 'Normal Priority';
      default: return 'Normal Priority';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'high-priority';
      case 'medium': return 'medium-priority';
      case 'normal': return 'normal-priority';
      default: return 'normal-priority';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getWorkingDays = () => {
    console.log('=== STUDENT WORKING DAYS DEBUG ===');
    console.log('Holidays data:', holidays);
    
    // Always use all 7 days as potential working days, then filter out holidays
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    // Filter out holiday days
    const filteredDays = allDays.filter(day => !holidays[day]);
    console.log('All days:', allDays);
    console.log('Filtered days (excluding holidays):', filteredDays);
    console.log('=== END STUDENT WORKING DAYS DEBUG ===');
    
    return filteredDays;
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const getAttendanceStats = () => {
    const totalRecords = attendanceRecords.length;
    const presentCount = attendanceRecords.filter(record => record.status === 'present').length;
    const absentCount = attendanceRecords.filter(record => record.status === 'absent').length;
    const attendancePercentage = totalRecords > 0 ? Math.round((presentCount / totalRecords) * 100) : 0;

    return {
      totalRecords,
      presentCount,
      absentCount,
      attendancePercentage
    };
  };

  const generateCalendar = () => {
    const year = selectedYear;
    const month = selectedMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Create a map of attendance records by date
    const attendanceMap = {};
    attendanceRecords.forEach(record => {
      try {
        const date = new Date(record.attendance_date);
        // Only map if the date is in the current month and year
        if (date.getMonth() === month && date.getFullYear() === year) {
          const day = date.getDate();
          attendanceMap[day] = record.status;
        }
      } catch (e) {
        console.error('Error parsing attendance date:', record.attendance_date);
      }
    });

    // Create calendar grid
    const calendar = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add day headers
    calendar.push(dayNames.map(day => ({ type: 'header', label: day })));
    
    // Add empty cells for days before the first day of the month
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      currentWeek.push({ type: 'empty', day: null });
    }
    
    // Add days of the month
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
      const status = attendanceMap[day];
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isHoliday = holidays && holidays[dateString];
      
      currentWeek.push({
        type: 'day',
        day,
        status,
        isWeekend,
        isToday,
        isHoliday
      });
      
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    }
    
    // Add remaining empty cells for the last week
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push({ type: 'empty', day: null });
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    // Combine headers and weeks
    return [...calendar, ...weeks];
  };

  const getPeriods = () => {
    if (!scheduleData || !scheduleData.schedule) return [];
    
    const periods = new Set();
    Object.keys(scheduleData.schedule).forEach(key => {
      const [day, period] = key.split('_');
      periods.add(parseInt(period));
    });
    
    return Array.from(periods).sort((a, b) => a - b);
  };

  const getDayName = (day) => {
    const dayNames = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };
    return dayNames[day] || day;
  };

  const getBreakAfterPeriod = (period) => {
    if (!schoolTimings || !schoolTimings.breaks) return null;
    
    const periodTimings = calculatePeriodTimings();
    const periodEnd = periodTimings[period]?.end;
    
    if (!periodEnd) return null;
    
    return schoolTimings.breaks.find(breakItem => {
      const breakStart = formatTime(breakItem.startTime);
      return breakStart === periodEnd;
    });
  };

  const calculatePeriodTimings = () => {
    if (!schoolTimings) return {};
    
    const startTime = schoolTimings.start_time || '08:00';
    const periodDuration = schoolTimings.period_time || 45;
    const breakDuration = schoolTimings.break_time || 15;
    const breaks = schoolTimings.breaks || [];
    
    const timings = {};
    let currentTime = startTime;
    
    // Ensure startTime is in HH:MM format
    if (!currentTime.includes(':')) {
      currentTime = currentTime.padStart(2, '0') + ':00';
    }
    
    for (let period = 1; period <= (schoolTimings.number_of_periods || 8); period++) {
      const startTimeFormatted = formatTime(currentTime);
      const endTime = addMinutes(currentTime, periodDuration);
      const endTimeFormatted = formatTime(endTime);
      
      timings[period] = {
        start: startTimeFormatted,
        end: endTimeFormatted,
        duration: periodDuration
      };
      
      // Check if there's a break that starts right after this period ends
      const breakAfterPeriod = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime);
        const periodEnd = formatTime(endTime);
        console.log(`Period ${period} ends at ${periodEnd}, checking break at ${breakStart}`);
        return breakStart === periodEnd;
      });
      
      if (breakAfterPeriod) {
        // If there's a break right after this period, next period starts after break ends
        currentTime = breakAfterPeriod.endTime;
        console.log(`Break found: ${breakAfterPeriod.name} from ${breakAfterPeriod.startTime} to ${breakAfterPeriod.endTime}`);
        console.log(`Next period will start at ${currentTime}`);
        
        // Ensure break end time is in HH:MM format
        if (!currentTime.includes(':')) {
          currentTime = currentTime.padStart(2, '0') + ':00';
        }
      } else {
        // No break after this period, so next period starts immediately after this period ends
        currentTime = endTime;
        console.log(`No break after period ${period}, next period starts at ${currentTime}`);
      }
    }
    
    return timings;
  };

  const addMinutes = (timeString, minutes) => {
    // Ensure timeString is in HH:MM format
    let formattedTime = timeString;
    if (!timeString.includes(':')) {
      formattedTime = timeString.padStart(2, '0') + ':00';
    }
    
    const [hours, mins] = formattedTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const calculatePeriodTimingsForPeriod = (periodNumber) => {
    if (!schoolTimings) {
      return { start: 'N/A', end: 'N/A', breakAfter: null };
    }
    
    const startTime = formatTime(schoolTimings.start_time);
    const periodDuration = schoolTimings.period_time || 40;
    const breaks = schoolTimings.breaks || [];
    
    // Calculate period start time: periods are consecutive
    // Period 1: startTime
    // Period 2: startTime + periodDuration
    // Period 3: startTime + 2 * periodDuration
    // etc.
    
    // Calculate cumulative time for periods before this one
    let periodStartTime = startTime;
    
    for (let i = 1; i < periodNumber; i++) {
      // Calculate when period i ends
      const periodIEnd = addMinutes(periodStartTime, periodDuration);
      
      // Check if there's a break after period i
      const breakAfter = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime);
        return breakStart === periodIEnd;
      });
      
      if (breakAfter) {
        // If there's a break, next period starts after break ends
        periodStartTime = formatTime(breakAfter.endTime);
      } else {
        // No break, so next period starts immediately after this period ends
        periodStartTime = periodIEnd;
      }
    }
    
    const periodEndTime = addMinutes(periodStartTime, periodDuration);
    
    // Check if there's a break after this period
    const breakAfter = breaks.find(breakItem => {
      const breakStart = formatTime(breakItem.startTime);
      return breakStart === periodEndTime;
    });
    
    return {
      start: periodStartTime,
      end: periodEndTime,
      breakAfter: breakAfter
    };
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // Handle null or undefined
    if (timeString === null || timeString === undefined) return 'N/A';
    
    // Convert to string if it's not already
    const timeStr = String(timeString).trim();
    
    // If empty string, return N/A
    if (!timeStr) return 'N/A';
    
    // Ensure we always show HH:MM format
    if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
      return timeStr;
    }
    
    // If it's missing minutes, add :00
    if (timeStr.match(/^\d{1,2}$/)) {
      return timeStr.padStart(2, '0') + ':00';
    }
    
    // Clean up any extra characters but keep HH:MM format
    const cleaned = timeStr.replace(/[^\d:]/g, '');
    if (cleaned.match(/^\d{1,2}:\d{2}$/)) {
      return cleaned;
    }
    if (cleaned.match(/^\d{1,2}$/)) {
      return cleaned.padStart(2, '0') + ':00';
    }
    
    // If all else fails, try to extract time pattern
    const timeMatch = cleaned.match(/(\d{1,2}):?(\d{0,2})/);
    if (timeMatch) {
      const hours = timeMatch[1].padStart(2, '0');
      const minutes = timeMatch[2] ? timeMatch[2].padStart(2, '0') : '00';
      return `${hours}:${minutes}`;
    }
    
    return 'N/A';
  };

  const handleTabChange = (tab) => {
    // Navigate to the appropriate URL
    navigate(`/student/${tab}`);
    setActiveTab(tab);
    if (tab === 'schedule') {
      fetchStudentSchedule();
      fetchSchoolTimings();
    }
    if (tab !== 'chat') {
      setSelectedTeacher(null);
      setChatMessages([]);
      setChatGroup(null);
      setShowSidebar(true);
    } else {
      setShowSidebar(true);
    }
  };

  const fetchStudentTeachers = async () => {
    // Check for required fields (school_id and class_id are required, section_id is optional)
    if (!studentData?.school_id || !studentData?.class_id) {
      console.log('Missing required student data for fetching teachers:', {
        school_id: studentData?.school_id,
        class_id: studentData?.class_id
      });
      setTeachers([]);
      setTeachersLoading(false);
      return;
    }

    try {
      setTeachersLoading(true);
      const classId = typeof studentData.class_id === 'string' ? parseInt(studentData.class_id, 10) : studentData.class_id;
      // Get section from either section_id or section field (can be null/undefined)
      const sectionId = studentData?.section_id || studentData?.section;
      const sectionIdStr = sectionId ? String(sectionId).trim() : null;
      
      console.log('Fetching teachers for student:', {
        school_id: studentData.school_id,
        class_id: classId,
        section_id: sectionIdStr || 'NOT SET (will show all teachers for class)'
      });

      // PRIMARY METHOD: Fetch teachers from classes table (subjects & teachers array)
      // This is the most reliable source as it matches what's shown on /school/classes page
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('id, subjects, teachers, subject_sections, class_teacher_id, assistant_teacher_id')
        .eq('id', classId)
        .eq('school_id', studentData.school_id)
        .single();

      const teachersMap = {}; // Map to store unique teachers with their subjects
      const teacherIdsSet = new Set(); // Track all teacher IDs to fetch

      if (!classError && classData) {
        // Parse subject_sections if it's a string
        let subjectSectionsObj = classData.subject_sections;
        if (subjectSectionsObj && typeof subjectSectionsObj === 'string') {
          try {
            subjectSectionsObj = JSON.parse(subjectSectionsObj);
          } catch (e) {
            console.error('Error parsing subject_sections:', e);
            subjectSectionsObj = {};
          }
        }

        // Process subjects and teachers arrays from classes table
        if (classData.subjects && Array.isArray(classData.subjects) && 
            classData.teachers && Array.isArray(classData.teachers)) {
          
          classData.subjects.forEach((subject, index) => {
            const teacherId = classData.teachers[index];
            if (teacherId) {
              const teacherIdNum = typeof teacherId === 'string' ? parseInt(teacherId, 10) : teacherId;
              
              // Check section filter if section_id is provided
              if (sectionIdStr) {
                // Check if this subject-teacher pair is for the student's section
                // subject_sections can be in format: {"subject_name_index": "section"} or {"subject_name": "section"}
                const subjectKey = `${subject}_${index}`;
                const subjectSection = subjectSectionsObj?.[subjectKey] || subjectSectionsObj?.[subject];
                
                // If subject_sections is specified, only include if it matches student's section
                // If subject_sections is not specified, include the teacher (they teach all sections)
                if (subjectSection) {
                  const subjectSectionStr = String(subjectSection).trim();
                  if (subjectSectionStr.toUpperCase() !== sectionIdStr.toUpperCase() && 
                      subjectSectionStr !== sectionIdStr) {
                    return; // Skip this teacher-subject pair if section doesn't match
                  }
                }
                // If no section specified in subject_sections, include the teacher
              }

              // Add teacher to map
              if (!teachersMap[teacherIdNum]) {
                teachersMap[teacherIdNum] = {
                  id: teacherIdNum,
                  subjects: []
                };
              }
              
              // Add subject if not already added
              if (subject && !teachersMap[teacherIdNum].subjects.includes(subject)) {
                teachersMap[teacherIdNum].subjects.push(subject);
              }
              
              teacherIdsSet.add(teacherIdNum);
            }
          });
        }

        // Also add class_teacher_id and assistant_teacher_id if they exist
        if (classData.class_teacher_id) {
          const classTeacherId = typeof classData.class_teacher_id === 'string' 
            ? parseInt(classData.class_teacher_id, 10) 
            : classData.class_teacher_id;
          if (!teachersMap[classTeacherId]) {
            teachersMap[classTeacherId] = {
              id: classTeacherId,
              subjects: []
            };
          }
          teacherIdsSet.add(classTeacherId);
        }

        if (classData.assistant_teacher_id) {
          const assistantTeacherId = typeof classData.assistant_teacher_id === 'string' 
            ? parseInt(classData.assistant_teacher_id, 10) 
            : classData.assistant_teacher_id;
          if (!teachersMap[assistantTeacherId]) {
            teachersMap[assistantTeacherId] = {
              id: assistantTeacherId,
              subjects: []
            };
          }
          teacherIdsSet.add(assistantTeacherId);
        }
      }

      // SECONDARY METHOD: Also fetch from class_schedules table to catch any additional teachers
      let scheduleQuery = supabase
        .from('class_schedules')
        .select('teacher_id, teacher_name, subject_name')
        .eq('school_id', studentData.school_id)
        .eq('class_id', classId);
      
      // Only filter by section if section_id is provided
      if (sectionIdStr) {
        scheduleQuery = scheduleQuery.eq('section_name', sectionIdStr);
      }
      
      const { data: scheduleTeachers, error: scheduleError } = await scheduleQuery;

      if (!scheduleError && scheduleTeachers && scheduleTeachers.length > 0) {
        scheduleTeachers.forEach(schedule => {
          if (schedule.teacher_id) {
            const teacherId = typeof schedule.teacher_id === 'string' 
              ? parseInt(schedule.teacher_id, 10) 
              : schedule.teacher_id;
            
            if (!teachersMap[teacherId]) {
              teachersMap[teacherId] = {
                id: teacherId,
                subjects: []
              };
            }
            
            // Add subject if not already added
            if (schedule.subject_name && !teachersMap[teacherId].subjects.includes(schedule.subject_name)) {
              teachersMap[teacherId].subjects.push(schedule.subject_name);
            }
            
            teacherIdsSet.add(teacherId);
          }
        });
      }

      // Fetch full teacher details for all found teachers
      const teacherIds = Array.from(teacherIdsSet);
      let teachersWithDetails = [];

      if (teacherIds.length > 0) {
        const { data: teacherDetails, error: teacherError } = await supabase
          .from('teachers')
          .select('id, first_name, last_name, email, phone, qualification, subjects, is_active')
          .eq('school_id', studentData.school_id)
          .eq('is_active', true)
          .in('id', teacherIds);

        if (!teacherError && teacherDetails) {
          teachersWithDetails = teacherDetails.map(teacher => {
            const mapData = teachersMap[teacher.id];
            // Use subjects from classes table if available, otherwise use teacher's subjects
            const subjects = mapData && mapData.subjects.length > 0 
              ? mapData.subjects 
              : (teacher.subjects || []);
            
            return {
              ...teacher,
              subjects: subjects
            };
          });
        }
      }

      // FALLBACK: If still no teachers found, check teachers table directly
      if (teachersWithDetails.length === 0) {
        const { data: allTeachers, error } = await supabase
          .from('teachers')
          .select(`
            id,
            first_name,
            last_name,
            email,
            phone,
            qualification,
            subjects,
            class_ids,
            class_sections,
            is_active
          `)
          .eq('school_id', studentData.school_id)
          .eq('is_active', true);

        if (!error && allTeachers) {
          // Filter teachers who are assigned to this class
          const matchingTeachers = allTeachers.filter(teacher => {
            // Check if teacher has this class in their class_ids array
            const hasClass = teacher.class_ids && Array.isArray(teacher.class_ids) && 
              (teacher.class_ids.includes(classId) || teacher.class_ids.includes(String(classId)) || 
               teacher.class_ids.includes(Number(classId)));

            if (!hasClass) return false;

            // If section_id is not set, show all teachers assigned to this class
            if (!sectionIdStr) {
              return true;
            }
            
            // Check if teacher has this section for this class in class_sections
            if (teacher.class_sections && typeof teacher.class_sections === 'object') {
              const classSections = teacher.class_sections[classId.toString()] || 
                                   teacher.class_sections[String(classId)] ||
                                   teacher.class_sections[classId] ||
                                   teacher.class_sections[Number(classId)];
              
              if (Array.isArray(classSections)) {
                const sectionMatch = classSections.some(sec => 
                  String(sec).trim().toUpperCase() === sectionIdStr.toUpperCase() ||
                  String(sec).trim() === sectionIdStr
                );
                if (sectionMatch) return true;
              }
            }

            // If teacher has the class but no section info, still show them
            return true;
          });

          // Remove duplicates
          const uniqueTeachers = matchingTeachers.filter((teacher, index, self) =>
            index === self.findIndex(t => t.id === teacher.id)
          );

          teachersWithDetails = uniqueTeachers.map(teacher => ({
            ...teacher,
            subjects: teacher.subjects || []
          }));
        }
      }

      console.log('Found teachers for student:', {
        count: teachersWithDetails.length,
        teachers: teachersWithDetails.map(t => ({
          id: t.id,
          name: `${t.first_name} ${t.last_name}`,
          subjects: t.subjects
        }))
      });
      
      // Add admin (school) to the list
      const adminContact = {
        id: 'admin',
        first_name: 'School',
        last_name: 'Admin',
        subjects: ['Administration'],
        isAdmin: true
      };
      
      setTeachers([adminContact, ...teachersWithDetails]);
      
      // Calculate unread messages for each teacher (including admin)
      await calculateUnreadCounts([adminContact, ...teachersWithDetails]);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  };

  const calculateUnreadCounts = async (teachersList) => {
    if (!studentData || !teachersList || teachersList.length === 0) return;

    const counts = {};
    
    for (const teacher of teachersList) {
      try {
        // Handle admin separately
        if (teacher.isAdmin) {
          // Find chat group with admin (school)
          const { data: groups } = await supabase
            .from('chat_groups')
            .select('id')
            .eq('school_id', studentData.school_id)
            .eq('group_type', 'custom')
            .ilike('name', `%Admin Chat%`);

          if (groups && groups.length > 0) {
            for (const group of groups) {
              const { data: members } = await supabase
                .from('chat_group_members')
                .select('*')
                .eq('group_id', group.id);

              const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === studentData.id.toString());
              const hasAdmin = members?.some(m => m.user_type === 'school');

              if (hasStudent && hasAdmin) {
                const lastReadKey = `lastRead_${studentData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);

                let query = supabase
                  .from('chat_messages')
                  .select('id', { count: 'exact', head: false })
                  .eq('group_id', group.id)
                  .eq('sender_type', 'school');

                if (lastReadTimestamp) {
                  query = query.gt('created_at', lastReadTimestamp);
                }

                const { count } = await query;
                counts['admin'] = count || 0;
                break;
              }
            }
          } else {
            counts['admin'] = 0;
          }
          continue;
        }

        // Find chat group for this teacher
        const { data: groups } = await supabase
          .from('chat_groups')
          .select('id')
          .eq('school_id', studentData.school_id)
          .eq('group_type', 'custom')
          .ilike('name', `%${studentData.first_name}%${teacher.first_name}%`);

        if (groups && groups.length > 0) {
          // Check if both are members
          for (const group of groups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', group.id);

            const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === studentData.id.toString());
            const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacher.id.toString());

            if (hasStudent && hasTeacher) {
              // Get last read timestamp from localStorage
              const lastReadKey = `lastRead_${studentData.id}_${teacher.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);

              // Count unread messages (messages from teacher after last read)
              let query = supabase
                .from('chat_messages')
                .select('id', { count: 'exact', head: false })
                .eq('group_id', group.id)
                .eq('sender_type', 'teacher')
                .eq('sender_id', teacher.id.toString());

              if (lastReadTimestamp) {
                query = query.gt('created_at', lastReadTimestamp);
              }

              const { count } = await query;
              counts[teacher.id] = count || 0;
              break;
            }
          }
        } else {
          counts[teacher.id] = 0;
        }
      } catch (err) {
        console.error(`Error calculating unread for teacher ${teacher.id}:`, err);
        counts[teacher.id] = 0;
      }
    }

    setUnreadCounts(counts);
  };

  const fetchStudentChatGroups = async () => {
    if (!studentData?.id || !studentData?.school_id) return;

    try {
      setLoadingGroups(true);
      
      // Find all chat groups where this student is a member
      const { data: memberships, error: membersError } = await supabase
        .from('chat_group_members')
        .select('group_id')
        .eq('user_type', 'student')
        .eq('user_id', studentData.id.toString());

      if (membersError) {
        console.error('Error fetching group memberships:', membersError);
        setChatGroups([]);
        return;
      }

      if (!memberships || memberships.length === 0) {
        setChatGroups([]);
        return;
      }

      const groupIds = memberships.map(m => m.group_id);

      // Fetch the actual groups
      const { data: groups, error: groupsError } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', studentData.school_id)
        .in('id', groupIds)
        .order('created_at', { ascending: false });

      if (groupsError) {
        console.error('Error fetching chat groups:', groupsError);
        setChatGroups([]);
        return;
      }

      // Enrich groups with last message info
      const enrichedGroups = await Promise.all(
        (groups || []).map(async (group) => {
          const { data: lastMessage } = await supabase
            .from('chat_messages')
            .select('created_at, content, sender_type, sender_id')
            .eq('group_id', group.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          // Get member count
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('id, user_type, display_name')
            .eq('group_id', group.id);

          return {
            ...group,
            lastMessage: lastMessage?.content || null,
            lastMessageTime: lastMessage?.created_at || null,
            lastMessageSender: lastMessage ? {
              type: lastMessage.sender_type,
              id: lastMessage.sender_id
            } : null,
            memberCount: members?.length || 0,
            members: members || []
          };
        })
      );

      setChatGroups(enrichedGroups);
    } catch (err) {
      console.error('Error fetching chat groups:', err);
      setChatGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadGroupChat = async (group) => {
    if (!group || !studentData) return;

    try {
      setGroupChatLoading(true);
      setSelectedChatGroup(group);
      setSelectedTeacher(null); // Clear teacher selection
      setChatGroup(null); // Clear individual chat group

      // Clean up individual chat channel when switching to group chat
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }

      // Fetch messages for this group
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching group messages:', error);
        return;
      }

      // Enrich messages with sender names
      const enrichedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          let senderName = 'Unknown';
          
          if (message.sender_type === 'student') {
            if (message.sender_id === studentData.id.toString()) {
              senderName = 'You';
            } else {
              const { data: student } = await supabase
                .from('students')
                .select('first_name, last_name')
                .eq('id', message.sender_id)
                .single();
              if (student) {
                senderName = `${student.first_name} ${student.last_name}`;
              }
            }
          } else if (message.sender_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (teacher) {
              senderName = `${teacher.first_name} ${teacher.last_name}`;
            }
          } else if (message.sender_type === 'parent') {
            const { data: parent } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (parent) {
              senderName = `${parent.first_name} ${parent.last_name}`;
            }
          }

          return {
            ...message,
            senderName
          };
        })
      );

      setGroupChatMessages(enrichedMessages);

      // Clean up any existing group chat channel
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }

      // Set up real-time subscription for new messages
      const channel = supabase
        .channel(`group-chat:${group.id}`, {
          config: {
            broadcast: { self: false }
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, async (payload) => {
          console.log('New group message received via real-time:', payload.new);
          const newMessage = payload.new;
          
          // Check if message already exists to avoid duplicates
          setGroupChatMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) {
              console.log('Message already exists, skipping duplicate');
              return prev;
            }
            return prev;
          });

          // Enrich the new message with sender name
          let senderName = 'Unknown';
          
          if (newMessage.sender_type === 'student') {
            if (newMessage.sender_id === studentData.id.toString()) {
              senderName = 'You';
            } else {
              const { data: student } = await supabase
                .from('students')
                .select('first_name, last_name')
                .eq('id', newMessage.sender_id)
                .single();
              if (student) {
                senderName = `${student.first_name} ${student.last_name}`;
              }
            }
          } else if (newMessage.sender_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', newMessage.sender_id)
              .single();
            if (teacher) {
              senderName = `${teacher.first_name} ${teacher.last_name}`;
            }
          } else if (newMessage.sender_type === 'parent') {
            const { data: parent } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', newMessage.sender_id)
              .single();
            if (parent) {
              senderName = `${parent.first_name} ${parent.last_name}`;
            }
          } else if (newMessage.sender_type === 'school') {
            senderName = 'School Admin';
          }

          const enrichedMessage = {
            ...newMessage,
            senderName
          };

          // Add enriched message to state
          setGroupChatMessages(prev => {
            const exists = prev.some(m => m.id === enrichedMessage.id);
            if (exists) return prev;
            console.log('Adding new message to group chat');
            // Sort messages by created_at to ensure proper order
            return [...prev, enrichedMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          });

          // Scroll to bottom when new message arrives
          setTimeout(() => {
            if (chatMessagesEndRef.current) {
              chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        })
        .subscribe((status) => {
          console.log('Group chat subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to group chat messages for group:', group.id);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Group chat channel subscription error');
          }
        });

      // Store channel reference for cleanup
      window.currentGroupChatChannel = channel;

      // Scroll to bottom after loading messages
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    } catch (err) {
      console.error('Error loading group chat:', err);
    } finally {
      setGroupChatLoading(false);
    }
  };

  const sendGroupMessage = async (e) => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
    }
    
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedChatGroup || !studentData) return;

    try {
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data: message, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: studentData.school_id,
          group_id: selectedChatGroup.id,
          sender_type: 'student',
          sender_id: studentData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) throw error;

      // Add to messages with sender name
      const newMessageWithSender = {
        ...message,
        senderName: 'You'
      };

      // Add message and sort by timestamp
      setGroupChatMessages(prev => {
        const exists = prev.some(m => m.id === newMessageWithSender.id);
        if (exists) return prev;
        return [...prev, newMessageWithSender].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      setNewMessage('');

      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending group message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    }).slice(0, 5);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Mark message as read
  const markMessageAsRead = async (messageId, message) => {
    if (!message || !studentData) return;
    
    // Only mark messages sent by others as read
    if (message.sender_type === 'student' && message.sender_id === studentData.id.toString()) {
      return; // Don't mark own messages
    }

    try {
      const readBy = message.read_by || [];
      const readerId = `${studentData.id}`;
      const readerType = 'student';
      
      // Check if already marked as read by this user
      const alreadyRead = readBy.some(
        reader => reader.user_id === readerId && reader.user_type === readerType
      );
      
      if (alreadyRead) return;

      // Add current user to read_by array
      const updatedReadBy = [
        ...readBy,
        {
          user_id: readerId,
          user_type: readerType,
          read_at: new Date().toISOString()
        }
      ];

      await supabase
        .from('chat_messages')
        .update({ read_by: updatedReadBy })
        .eq('id', messageId);

      // Update local state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, read_by: updatedReadBy }
            : msg
        )
      );
      
      setGroupChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, read_by: updatedReadBy }
            : msg
        )
      );
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  // Get message status indicator
  const getMessageStatus = (message) => {
    if (!message || message.sender_type !== 'student' || message.sender_id !== studentData.id.toString()) {
      return null; // Only show status for own messages
    }

    const readBy = message.read_by || [];
    
    // Check if recipient has read the message
    if (selectedTeacher) {
      const recipientId = selectedTeacher.isAdmin 
        ? studentData.school_id.toString() 
        : selectedTeacher.id.toString();
      const recipientType = selectedTeacher.isAdmin ? 'school' : 'teacher';
      
      const isRead = readBy.some(
        reader => reader.user_id === recipientId && reader.user_type === recipientType
      );
      
      if (isRead) {
        return { status: 'read', icon: '✓✓', color: '#4F46E5' }; // Blue double check
      }
    }
    
    // Message is delivered (exists in DB) but not read
    return { status: 'delivered', icon: '✓✓', color: '#9CA3AF' }; // Gray double check
  };

  const loadOrCreateChatGroup = async () => {
    if (!selectedTeacher || !studentData) return;

    try {
      setChatLoading(true);
      
      // Handle admin chat
      if (selectedTeacher.isAdmin) {
        const groupName = `Admin Chat: ${studentData.first_name} ${studentData.last_name}`;
        
        // Check if admin chat group already exists
        let { data: existingGroups, error: groupError } = await supabase
          .from('chat_groups')
          .select('*')
          .eq('school_id', studentData.school_id)
          .eq('group_type', 'custom')
          .ilike('name', `%Admin Chat%${studentData.first_name}%`);

        let group = null;

        if (existingGroups && existingGroups.length > 0) {
          // Check if both student and admin are members
          for (const g of existingGroups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', g.id);

            const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === studentData.id.toString());
            const hasAdmin = members?.some(m => m.user_type === 'school' && m.user_id === studentData.school_id.toString());

            if (hasStudent && hasAdmin) {
              group = g;
              break;
            }
          }
        }

        // Create new admin chat group if doesn't exist
        if (!group) {
          const { data: newGroup, error: createError } = await supabase
            .from('chat_groups')
            .insert({
              school_id: studentData.school_id,
              name: groupName,
              group_type: 'custom',
              created_by: 'student'
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating admin chat group:', createError);
            alert('Failed to create chat. Please try again.');
            return;
          }

          group = newGroup;

          // Add student as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'student',
              user_id: studentData.id.toString(),
              display_name: `${studentData.first_name} ${studentData.last_name}`
            });

          // Add admin (school) as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'school',
              user_id: studentData.school_id.toString(),
              display_name: 'School Admin'
            });
        }

        setChatGroup(group);

        // Load messages
        const { data: messages, error: messagesError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('group_id', group.id)
          .order('created_at', { ascending: true });

        if (messagesError) {
          console.error('Error loading messages:', messagesError);
        } else {
          setChatMessages(messages || []);
          
          // Mark messages as read
          if (messages && messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            const lastReadKey = `lastRead_${studentData.id}_admin`;
            localStorage.setItem(lastReadKey, lastMessage.created_at);
            
            setUnreadCounts(prev => ({
              ...prev,
              'admin': 0
            }));
          }
        }

        // Clean up existing subscription
        if (window.currentChatChannel) {
          supabase.removeChannel(window.currentChatChannel);
          window.currentChatChannel = null;
        }

        // Set up real-time subscription
        const channel = supabase
          .channel(`chat:${group.id}`)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `group_id=eq.${group.id}`
          }, (payload) => {
            const newMessage = payload.new;
            
            setChatMessages(prev => {
              const exists = prev.some(m => m.id === newMessage.id);
              if (exists) return prev;
              
              if (newMessage.sender_type === 'school') {
                const lastReadKey = `lastRead_${studentData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                
                if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                  setUnreadCounts(prevCounts => ({
                    ...prevCounts,
                    'admin': (prevCounts['admin'] || 0) + 1
                  }));
                }
              }
              
              return [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            });

            setTimeout(() => {
              if (chatMessagesEndRef.current) {
                chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          })
          .subscribe();

        window.currentChatChannel = channel;

        setTimeout(() => {
          if (chatMessagesEndRef.current) {
            chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        
        setChatLoading(false);
        return;
      }

      // Regular teacher chat (existing code)
      const groupName = `Chat: ${studentData.first_name} ${studentData.last_name} - ${selectedTeacher.first_name} ${selectedTeacher.last_name}`;

      // Check if chat group already exists
      let { data: existingGroups, error: groupError } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', studentData.school_id)
        .eq('group_type', 'custom')
        .ilike('name', `%${studentData.first_name}%${selectedTeacher.first_name}%`);

      let group = null;

      if (existingGroups && existingGroups.length > 0) {
        // Check if both student and teacher are members
        for (const g of existingGroups) {
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('*')
            .eq('group_id', g.id);

          const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === studentData.id.toString());
          const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === selectedTeacher.id.toString());

          if (hasStudent && hasTeacher) {
            group = g;
            break;
          }
        }
      }

      // Create new group if doesn't exist
      if (!group) {
        const { data: newGroup, error: createError } = await supabase
          .from('chat_groups')
          .insert({
            school_id: studentData.school_id,
            name: groupName,
            group_type: 'custom',
            created_by: 'student'
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating chat group:', createError);
          alert('Failed to create chat. Please try again.');
          return;
        }

        group = newGroup;

        // Add student as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'student',
            user_id: studentData.id.toString(),
            display_name: `${studentData.first_name} ${studentData.last_name}`
          });

        // Add teacher as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'teacher',
            user_id: selectedTeacher.id.toString(),
            display_name: `${selectedTeacher.first_name} ${selectedTeacher.last_name}`
          });
      }

      setChatGroup(group);

      // Load messages
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else {
        setChatMessages(messages || []);
        
        // Mark messages as read - update last read timestamp
        if (messages && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          const lastReadKey = `lastRead_${studentData.id}_${selectedTeacher.id}`;
          localStorage.setItem(lastReadKey, lastMessage.created_at);
          
          // Update unread count to 0 for this teacher
          setUnreadCounts(prev => ({
            ...prev,
            [selectedTeacher.id]: 0
          }));
        }
      }

      // Clean up any existing subscription first
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
      // Clean up group chat channel when switching to individual chat
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }

      // Set up real-time subscription for new messages
      const channel = supabase
        .channel(`chat:${group.id}`, {
          config: {
            broadcast: { self: false }
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, (payload) => {
          console.log('New message received via real-time:', payload.new);
          const newMessage = payload.new;
          
          setChatMessages(prev => {
            // Check if message already exists to avoid duplicates
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) {
              console.log('Message already exists, skipping duplicate');
              return prev;
            }
            console.log('Adding new message to chat');
            
            // If message is from teacher and chat is not currently open, increment unread count
            if (newMessage.sender_type === 'teacher' && selectedTeacher) {
              const lastReadKey = `lastRead_${studentData.id}_${selectedTeacher.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);
              
              // Only increment if message is newer than last read
              if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                setUnreadCounts(prevCounts => ({
                  ...prevCounts,
                  [selectedTeacher.id]: (prevCounts[selectedTeacher.id] || 0) + 1
                }));
              }
            }
            
            // Sort messages by created_at to ensure proper order
            return [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          });

          // Scroll to bottom when new message arrives
          setTimeout(() => {
            if (chatMessagesEndRef.current) {
              chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        })
        .subscribe((status) => {
          console.log('Subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to chat messages for group:', group.id);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Channel subscription error');
          }
        });

      // Store channel reference for cleanup
      window.currentChatChannel = channel;

      // Scroll to bottom after loading messages
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error loading chat:', err);
      alert('Failed to load chat. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  // Upload files to Supabase storage
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadedUrls = [];
    setUploadingFiles(true);
    
    try {
      const bucketName = 'chat-uploads';
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${studentData.school_id}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          if (uploadError.message.includes('Bucket not found')) {
            alert('Storage bucket not found. Please create "chat-uploads" bucket in Supabase Storage.');
            continue;
          }
          continue;
        }
        
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        
        uploadedUrls.push({
          url: urlData.publicUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedUrls;
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !chatGroup || !studentData) return;

    try {
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: studentData.school_id,
          group_id: chatGroup.id,
          sender_type: 'student',
          sender_id: studentData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }

      setChatMessages(prev => {
        const exists = prev.some(m => m.id === data.id);
        if (exists) return prev;
        return [...prev, data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      setNewMessage('');

      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  // AI Chat function - Uses Google Gemini API only
  const sendAiMessage = async () => {
    if (!aiChatInput.trim() || aiChatLoading) return;

    const userMessage = aiChatInput.trim();
    const timestamp = new Date().toISOString();

    // Add user message to chat
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      timestamp
    };
    setAiChatMessages(prev => [...prev, userMsg]);
    setAiChatInput('');
    setAiChatLoading(true);

    try {
      // Try Google Gemini API first
      let aiResponse = null;
      try {
        aiResponse = await tryGoogleGemini(userMessage);
      } catch (geminiError) {
        console.log('Gemini API not available, using smart assistant:', geminiError);
        // Fall through to use smart assistant
      }
      
      // If Gemini fails, use smart rule-based assistant
      if (!aiResponse) {
        aiResponse = getSmartAssistantResponse(userMessage);
      }

      // Add AI response to chat
      const aiMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      
      // Fallback to smart assistant
      const fallbackResponse = getSmartAssistantResponse(userMessage);
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: fallbackResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, errorMsg]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // List available Gemini models
  const listAvailableModels = async (apiKey) => {
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      );
      if (response.ok) {
        const data = await response.json();
        return data.models?.map(m => m.name) || [];
      }
    } catch (err) {
      console.error('Error listing models:', err);
    }
    return [];
  };

  // Use Google Gemini API
  const tryGoogleGemini = async (question) => {
    // Get API key from localStorage (default key is set automatically)
    const geminiApiKey = localStorage.getItem('gemini_api_key') || 'AIzaSyBIRBgSh5JpYKU2-9sW4-XAeZNyRK5HzCg';
    
    // First, try to get available models
    const availableModels = await listAvailableModels(geminiApiKey);
    console.log('Available Gemini models:', availableModels);
    
    // Try different API endpoints and models
    // Prioritize newer models and available models from API
    // Filter out embedding models (they don't support generateContent)
    const textGenerationModels = availableModels.length > 0 
      ? availableModels
          .filter(modelName => 
            !modelName.includes('embedding') && 
            !modelName.includes('imagen') && 
            !modelName.includes('veo') &&
            !modelName.includes('aqa')
          )
          .map(modelName => ({
            version: 'v1beta',
            model: modelName.replace('models/', '')
          }))
      : [];
    
    const apiConfigs = [
      // First, try available models from API (most reliable)
      ...textGenerationModels,
      // Then try latest stable models (v2.5)
      { version: 'v1beta', model: 'gemini-2.5-flash' },
      { version: 'v1beta', model: 'gemini-2.5-pro' },
      { version: 'v1beta', model: 'gemini-flash-latest' },
      { version: 'v1beta', model: 'gemini-pro-latest' },
      // Try v2.0 models as fallback
      { version: 'v1beta', model: 'gemini-2.0-flash' },
      { version: 'v1beta', model: 'gemini-2.0-pro-exp' },
      // Legacy models (last resort)
      { version: 'v1beta', model: 'gemini-1.5-flash' },
      { version: 'v1beta', model: 'gemini-1.5-pro' },
    ];
    
    for (const config of apiConfigs) {
      try {
        const url = `https://generativelanguage.googleapis.com/${config.version}/models/${config.model}:generateContent?key=${geminiApiKey}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `You are a helpful and friendly AI tutor for students. Answer their questions clearly, in an educational manner, and be encouraging. Provide detailed explanations when needed, but keep responses clear and easy to understand.

Student Question: ${question}

Provide a helpful, educational response:`
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024,
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(`Gemini API HTTP error (${config.version}/${config.model}):`, response.status, errorData);
          
          // Log detailed error for debugging
          if (errorData.error) {
            console.error('Detailed error:', {
              code: errorData.error.code,
              message: errorData.error.message,
              status: errorData.error.status
            });
          }
          
          // If it's a model not found error (404), try next config
          if (response.status === 404) {
            continue; // Try next config
          }
          
          // If it's authentication error (401/403), don't try other configs
          if (response.status === 401 || response.status === 403) {
            throw new Error(errorData.error?.message || `Authentication error: ${response.status}. Please check your API key.`);
          }
          
          // If it's the last config, return null to use fallback
          if (config === apiConfigs[apiConfigs.length - 1]) {
            return null; // Use fallback instead of throwing
          }
          continue;
        }

        const data = await response.json();
        console.log('Gemini API response:', data);
        
        if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
          return data.candidates[0].content.parts[0].text.trim();
        } else if (data.error) {
          console.error('Gemini API error in response:', data.error);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error(data.error.message || 'Failed to get response');
        } else {
          console.error('Unexpected response format:', data);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error('Unexpected response format from API');
        }
      } catch (err) {
        console.error(`Gemini API error (${config.version}/${config.model}):`, err);
        // If it's the last config, throw the error
        if (config === apiConfigs[apiConfigs.length - 1]) {
          throw err;
        }
        // Otherwise, continue to next config
        continue;
      }
    }
    
    // If we get here, all configs failed - return null to use fallback
    return null;
  };

  // Smart Rule-Based Assistant - Works without API
  const getSmartAssistantResponse = (question) => {
    const lowerQuestion = question.toLowerCase().trim();
    
    // Math calculations
    if (lowerQuestion.match(/\d+\s*[+\-*/×÷]\s*\d+/)) {
      try {
        const mathMatch = question.match(/(\d+)\s*([+\-*/×÷])\s*(\d+)/);
        if (mathMatch) {
          const num1 = parseFloat(mathMatch[1]);
          const op = mathMatch[2] === '×' ? '*' : mathMatch[2] === '÷' ? '/' : mathMatch[2];
          const num2 = parseFloat(mathMatch[3]);
          let result;
          switch(op) {
            case '+': result = num1 + num2; break;
            case '-': result = num1 - num2; break;
            case '*': result = num1 * num2; break;
            case '/': result = num2 !== 0 ? (num1 / num2).toFixed(2) : 'undefined (division by zero)'; break;
            default: break;
          }
          if (result !== undefined) {
            return `The answer is: **${result}**\n\n${num1} ${op} ${num2} = ${result}`;
          }
        }
      } catch (e) {}
    }

    // Study tips
    if (lowerQuestion.includes('how to study') || lowerQuestion.includes('study tips') || lowerQuestion.includes('study better')) {
      return `Here are effective study tips:\n\n1. **Create a Study Schedule**: Plan your study time and stick to it\n2. **Find Your Learning Style**: Visual, auditory, or kinesthetic\n3. **Take Regular Breaks**: Study for 25-30 minutes, then take a 5-minute break\n4. **Active Recall**: Test yourself instead of just re-reading\n5. **Teach Someone Else**: Explaining helps you understand better\n6. **Use Mnemonics**: Create memory aids for difficult concepts\n7. **Stay Organized**: Keep notes neat and organized\n8. **Get Enough Sleep**: Your brain needs rest to retain information\n9. **Eliminate Distractions**: Find a quiet place to study\n10. **Review Regularly**: Don't cram - review material over time\n\nWould you like tips for a specific subject?`;
    }

    // Exam preparation
    if (lowerQuestion.includes('exam') || lowerQuestion.includes('test preparation') || lowerQuestion.includes('how to prepare')) {
      return `Exam Preparation Guide:\n\n**Before the Exam:**\n1. Review all notes and materials\n2. Practice with past papers or sample questions\n3. Create summary notes or flashcards\n4. Get 7-8 hours of sleep\n5. Eat a healthy breakfast\n\n**During the Exam:**\n1. Read all questions first\n2. Start with easier questions to build confidence\n3. Manage your time - don't spend too long on one question\n4. Show your work for math problems\n5. Review your answers if time permits\n\n**After the Exam:**\n1. Don't stress about what you could have done\n2. Learn from mistakes for next time\n3. Celebrate your effort!\n\nNeed help with a specific subject?`;
    }

    // Homework help
    if (lowerQuestion.includes('homework') || lowerQuestion.includes('assignment') || lowerQuestion.includes('help with')) {
      return `Homework Help Tips:\n\n1. **Read Instructions Carefully**: Make sure you understand what's required\n2. **Break It Down**: Divide large assignments into smaller tasks\n3. **Start Early**: Don't wait until the last minute\n4. **Find a Quiet Space**: Eliminate distractions\n5. **Use Your Resources**: Textbooks, notes, online resources\n6. **Ask for Help**: Don't hesitate to ask teachers or classmates\n7. **Take Breaks**: Work for 30-45 minutes, then take a 10-minute break\n8. **Review Before Submitting**: Check your work for errors\n\n**For specific subjects:**\n- Math: Show all steps, check your calculations\n- Science: Understand concepts, not just memorize\n- English: Plan your essay, proofread carefully\n- History: Focus on dates, events, and their significance\n\nWhat subject do you need help with?`;
    }

    // Math help
    if (lowerQuestion.includes('math') || lowerQuestion.includes('mathematics') || lowerQuestion.includes('calculate') || lowerQuestion.includes('solve')) {
      return `Math Help Guide:\n\n**Problem-Solving Steps:**\n1. Read the problem carefully\n2. Identify what you're asked to find\n3. List all given information\n4. Choose the right formula or method\n5. Show your work step by step\n6. Check your answer\n\n**Common Math Topics:**\n- **Algebra**: Solving equations, working with variables\n- **Geometry**: Shapes, angles, area, perimeter\n- **Fractions**: Adding, subtracting, multiplying, dividing\n- **Decimals**: Place value, operations\n- **Percentages**: Finding percentages, discounts\n\n**Tips:**\n- Practice regularly\n- Understand concepts, don't just memorize\n- Show all your work\n- Check answers by working backwards\n\nCan you share the specific math problem you're working on?`;
    }

    // Science help
    if (lowerQuestion.includes('science') || lowerQuestion.includes('biology') || lowerQuestion.includes('chemistry') || lowerQuestion.includes('physics')) {
      return `Science Study Tips:\n\n**General Science:**\n1. Understand concepts, not just memorize facts\n2. Make connections between topics\n3. Use diagrams and visual aids\n4. Conduct experiments when possible\n5. Ask "why" and "how" questions\n\n**Biology:**\n- Study life processes and living organisms\n- Use diagrams for anatomy\n- Understand relationships in ecosystems\n\n**Chemistry:**\n- Learn the periodic table\n- Understand chemical reactions\n- Practice balancing equations\n\n**Physics:**\n- Understand formulas and when to use them\n- Practice problem-solving\n- Visualize concepts\n\nWhat specific science topic are you studying?`;
    }

    // Time management
    if (lowerQuestion.includes('time') && (lowerQuestion.includes('manage') || lowerQuestion.includes('organize') || lowerQuestion.includes('schedule'))) {
      return `Time Management Tips:\n\n1. **Use a Planner**: Write down all assignments and deadlines\n2. **Prioritize**: Do important and urgent tasks first\n3. **Break Tasks Down**: Large projects into smaller steps\n4. **Set Goals**: Daily, weekly, and monthly goals\n5. **Avoid Procrastination**: Start tasks immediately\n6. **Limit Distractions**: Put phone away, find quiet space\n7. **Use Time Blocks**: Dedicate specific times for specific subjects\n8. **Take Breaks**: Rest helps you stay focused\n9. **Review Daily**: Check what you accomplished\n10. **Be Flexible**: Adjust your schedule as needed\n\nWould you like help creating a study schedule?`;
    }

    // Motivation/encouragement
    if (lowerQuestion.includes('motivated') || lowerQuestion.includes('tired') || lowerQuestion.includes('difficult') || lowerQuestion.includes('hard')) {
      return `You've got this! 💪\n\n**Staying Motivated:**\n1. Set small, achievable goals\n2. Reward yourself after completing tasks\n3. Remember your long-term goals\n4. Find a study buddy\n5. Take care of yourself (sleep, exercise, eat well)\n6. Break difficult tasks into smaller parts\n7. Celebrate small wins\n8. Remember: Every expert was once a beginner\n\n**When Things Get Tough:**\n- Take a short break\n- Talk to a teacher or friend\n- Remember why you're studying\n- Don't give up - persistence pays off!\n\nWhat specific challenge are you facing? I'm here to help!`;
    }

    // General greeting
    if (lowerQuestion.match(/^(hi|hello|hey|good morning|good afternoon|good evening)/)) {
      return `Hello! 👋 I'm your AI Study Assistant!\n\nI can help you with:\n- 📚 Study tips and strategies\n- 📝 Homework help\n- 🧮 Math problems\n- 🔬 Science concepts\n- 📖 Exam preparation\n- ⏰ Time management\n- 💡 Learning techniques\n\nWhat would you like help with today?`;
    }

    // General educational response
    return `I'm here to help with your studies! 📚\n\nI can assist with:\n- Study tips and techniques\n- Homework help\n- Math problem solving\n- Science concepts\n- Exam preparation\n- Time management\n\nCould you be more specific about what you need help with? For example:\n- "How do I solve quadratic equations?"\n- "What are study tips for exams?"\n- "Help me with my math homework"\n\nOr try asking your teacher for immediate help with specific questions!`;
  };

  // Fetch transport data
  const fetchTransportData = async () => {
    if (!studentData) return;
    
    setTransportLoading(true);
    try {
      // Check if student has transport enabled
      if (!studentData.transport_service_enabled) {
        setTransportData(null);
        setTransportLoading(false);
        return;
      }

      // Fetch latest student data to get seat number
      let currentStudentData = studentData;
      if (studentData.id) {
        const { data: latestStudent, error: studentError } = await supabase
          .from('students')
          .select('id, bus_seat_number')
          .eq('id', studentData.id)
          .single();
        
        if (!studentError && latestStudent) {
          currentStudentData = { ...studentData, bus_seat_number: latestStudent.bus_seat_number };
        }
      }

      // Fetch route information
      let routeData = null;
      if (currentStudentData.route_id) {
        const { data: route, error: routeError } = await supabase
          .from('routes')
          .select(`
            *,
            transport_vehicles:vehicle_id (
              id,
              bus_number,
              driver_name,
              capacity
            )
          `)
          .eq('id', studentData.route_id)
          .single();

        if (!routeError && route) {
          routeData = {
            ...route,
            vehicle: route.transport_vehicles || null,
            seatNumber: currentStudentData.bus_seat_number || null
          };

          // Fetch pickup location
          if (studentData.pickup_location_id) {
            const { data: pickupLocation, error: locationError } = await supabase
              .from('pickup_locations')
              .select('*')
              .eq('id', studentData.pickup_location_id)
              .single();

            if (!locationError && pickupLocation) {
              routeData.pickupLocation = pickupLocation;
            }
          }

          // Fetch all pickup locations for this route
          const { data: routeLocations, error: routeLocError } = await supabase
            .from('route_pickup_locations')
            .select('sequence_order, pickup_location_id')
            .eq('route_id', route.id)
            .order('sequence_order', { ascending: true });

          if (!routeLocError && routeLocations) {
            const locationIds = routeLocations.map(rl => rl.pickup_location_id);
            const { data: locations, error: locsError } = await supabase
              .from('pickup_locations')
              .select('id, location_name, address, latitude, longitude')
              .in('id', locationIds);

            if (!locsError && locations) {
              // Geocode addresses that don't have coordinates
              const geocodeAddress = async (address, locationName) => {
                try {
                  const searchQuery = locationName && address 
                    ? `${locationName}, ${address}, Rawalpindi, Pakistan`
                    : address 
                    ? `${address}, Rawalpindi, Pakistan`
                    : locationName
                    ? `${locationName}, Rawalpindi, Pakistan`
                    : null;

                  if (!searchQuery) return null;

                  const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
                    {
                      headers: {
                        'User-Agent': 'StudentPortal/1.0'
                      }
                    }
                  );
                  
                  const data = await response.json();
                  
                  if (data && data.length > 0) {
                    return {
                      latitude: parseFloat(data[0].lat),
                      longitude: parseFloat(data[0].lon)
                    };
                  }
                  return null;
                } catch (error) {
                  console.error('Geocoding error:', error);
                  return null;
                }
              };

              // Process locations and geocode if needed
              routeData.allPickupLocations = await Promise.all(
                routeLocations.map(async (routeLoc) => {
                  const location = locations.find(loc => loc.id === routeLoc.pickup_location_id);
                  if (!location) return null;

                  let finalLocation = {
                    ...location,
                    sequence_order: routeLoc.sequence_order
                  };

                  // If location doesn't have coordinates, try to geocode
                  if (!location.latitude || !location.longitude) {
                    const coords = await geocodeAddress(location.address, location.location_name);
                    if (coords) {
                      finalLocation.latitude = coords.latitude.toString();
                      finalLocation.longitude = coords.longitude.toString();
                    }
                  }

                  return finalLocation;
                })
              );
              routeData.allPickupLocations = routeData.allPickupLocations.filter(Boolean);
            }
          }

          // Get driver information if vehicle has driver_name
          if (routeData.vehicle && routeData.vehicle.driver_name) {
            const { data: driver, error: driverError } = await supabase
              .from('transport_persons')
              .select('*')
              .eq('name', routeData.vehicle.driver_name)
              .single();

            if (!driverError && driver) {
              routeData.driver = driver;
            }
          }
        }
      }

      setTransportData(routeData);
      
      // Try to get bus location
      await updateBusLocation();
    } catch (error) {
      console.error('Error fetching transport data:', error);
    } finally {
      setTransportLoading(false);
    }
  };

  // Update bus location
  const updateBusLocation = async () => {
    if (!transportData || !transportData.id) {
      return;
    }

    try {
      const routeId = transportData.id;
      console.log('Checking for active route:', routeId);
      
      // Check if driver has started this route and location is available
      // Check localStorage for active route data (stored by transporter dashboard)
      const activeRouteKey = `activeRoute_${routeId}`;
      const activeRouteData = localStorage.getItem(activeRouteKey);
      
      if (activeRouteData) {
        try {
          const routeInfo = JSON.parse(activeRouteData);
          console.log('Found active route data:', routeInfo);
          
          // Check if location data is recent (within last 10 minutes - increased for testing)
          const locationTimestamp = new Date(routeInfo.timestamp);
          const now = new Date();
          const timeDiff = (now - locationTimestamp) / 1000 / 60; // difference in minutes
          
          console.log('Location age (minutes):', timeDiff);
          
          if (timeDiff < 10 && routeInfo.latitude && routeInfo.longitude) {
            // Location is recent and valid
            console.log('Setting bus location:', routeInfo.latitude, routeInfo.longitude);
            setBusLocation({
              lat: parseFloat(routeInfo.latitude),
              lng: parseFloat(routeInfo.longitude),
              timestamp: routeInfo.timestamp
            });
            return;
          } else {
            console.log('Location too old or invalid');
            // Location is too old, clear it
            localStorage.removeItem(activeRouteKey);
          }
        } catch (parseError) {
          console.error('Error parsing active route data:', parseError);
        }
      } else {
        console.log('No active route data found for key:', activeRouteKey);
      }
      
      // If no active route found, check all localStorage keys for any active route
      // This helps if the route ID format is slightly different
      let foundActiveRoute = false;
      console.log('Checking all localStorage keys...');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('activeRoute_')) {
          try {
            const routeData = JSON.parse(localStorage.getItem(key));
            console.log('Found route key:', key, routeData);
            // Check if this route matches the student's route by checking driver name
            if (transportData.vehicle && transportData.vehicle.driver_name && 
                routeData.driverName === transportData.vehicle.driver_name) {
              const locationTimestamp = new Date(routeData.timestamp);
              const now = new Date();
              const timeDiff = (now - locationTimestamp) / 1000 / 60;
              
              console.log('Driver name matches, location age:', timeDiff);
              
              if (timeDiff < 10 && routeData.latitude && routeData.longitude) {
                console.log('Setting bus location from matched route:', routeData.latitude, routeData.longitude);
                setBusLocation({
                  lat: parseFloat(routeData.latitude),
                  lng: parseFloat(routeData.longitude),
                  timestamp: routeData.timestamp
                });
                foundActiveRoute = true;
                break;
              }
            }
          } catch (e) {
            console.error('Error parsing route data:', e);
            // Skip invalid entries
            continue;
      }
    }
      }
      
      // If no active route found, don't set location (will show "not available")
      if (!foundActiveRoute) {
        console.log('No active route found');
        setBusLocation(null);
      }
    } catch (error) {
      console.error('Error updating bus location:', error);
      setBusLocation(null);
    }
  };

  // Save API key
  const handleSaveApiKey = () => {
    if (apiKeyInput.trim() && !apiKeyInput.includes('...')) {
      localStorage.setItem('gemini_api_key', apiKeyInput.trim());
      setHasApiKey(true);
      setShowApiKeySettings(false);
      setApiKeyInput('');
      alert('✅ API key saved successfully! You\'ll now get better AI responses.');
    }
  };

  // Remove API key
  const handleRemoveApiKey = () => {
    if (window.confirm('Are you sure you want to remove your API key?')) {
      localStorage.removeItem('gemini_api_key');
      setHasApiKey(false);
      setApiKeyInput('');
      setShowApiKeySettings(false);
      alert('API key removed. The assistant will still work with offline responses.');
    }
  };

  // Open API key settings
  const handleOpenApiKeySettings = () => {
    const storedKey = localStorage.getItem('gemini_api_key');
    if (storedKey) {
      setApiKeyInput(storedKey.substring(0, 10) + '...' + storedKey.substring(storedKey.length - 4));
    } else {
      setApiKeyInput('');
    }
    setShowApiKeySettings(true);
  };

  if (loading) {
    return (
      <div className="student-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="student-dashboard">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <p>Student data not found. Please login again.</p>
          <button onClick={() => navigate('/')} className="login-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      {/* Top Header Bar */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-info">
            <div className="school-name-header" style={{ color: '#FF8B3D', fontWeight: '900', fontSize: '20px' }}>
              {studentData?.schools?.school_name || studentData?.school_name || 'School'}
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="user-profile desktop-only">
            <div className="user-avatar">
              {studentData.first_name?.charAt(0).toUpperCase()}{studentData.last_name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details-header">
              <div className="user-name-header">{studentData.first_name} {studentData.last_name}</div>
              <div className="user-role-header">Student</div>
            </div>
          </div>
          <button className="logout-btn-header desktop-only" onClick={handleLogout}>
            Logout
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            style={{ color: '#FF8B3D', fontWeight: '900' }}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info">
                <div className="user-avatar">
                  {studentData.first_name?.charAt(0).toUpperCase()}{studentData.last_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="mobile-user-name">{studentData.first_name} {studentData.last_name}</div>
                  <div className="mobile-user-role">Student</div>
                </div>
              </div>
              <button className="mobile-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                Dashboard
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'fees' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('fees');
                  setMobileMenuOpen(false);
                }}
              >
                Fees
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'attendance' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('attendance');
                  setMobileMenuOpen(false);
                }}
              >
                Attendance
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'homework' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('homework');
                  setMobileMenuOpen(false);
                }}
              >
                Homework
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'exam-schedule' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('exam-schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Tests & Exams
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'results' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('results');
                  setMobileMenuOpen(false);
                }}
              >
                Results
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('chat');
                  setMobileMenuOpen(false);
                }}
              >
                Chat
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'transport' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('transport');
                  setMobileMenuOpen(false);
                }}
              >
                Transport
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'meals' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('meals');
                  setMobileMenuOpen(false);
                }}
              >
                Meals
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'policies' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('policies');
                  setMobileMenuOpen(false);
                }}
              >
                Policies
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'contact-us' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('contact-us');
                  setMobileMenuOpen(false);
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="nav-tabs">
        <button 
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          <span className="tab-text">Dashboard</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          <span className="tab-text">Schedule</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'fees' ? 'active' : ''}`}
          onClick={() => handleTabChange('fees')}
        >
          <span className="tab-text">Fees</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => handleTabChange('attendance')}
        >
          <span className="tab-text">Attendance</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'homework' ? 'active' : ''}`}
          onClick={() => handleTabChange('homework')}
        >
          <span className="tab-text">Homework</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'exam-schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('exam-schedule')}
        >
          <span className="tab-text">Tests & Exams</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => handleTabChange('results')}
        >
          <span className="tab-text">Results</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <span className="tab-text">Chat</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'transport' ? 'active' : ''}`}
          onClick={() => handleTabChange('transport')}
        >
          <span className="tab-text">Transport</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'meals' ? 'active' : ''}`}
          onClick={() => handleTabChange('meals')}
        >
          <span className="tab-text">Meals</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => handleTabChange('policies')}
        >
          <span className="tab-text">Policies</span>
        </button>
        <button 
          className={`nav-tab ${activeTab === 'contact-us' ? 'active' : ''}`}
          onClick={() => handleTabChange('contact-us')}
        >
          <span className="tab-text">Contact Us</span>
        </button>
      </div>

      <main className="dashboard-main">
        {activeTab === 'dashboard' && (
          <>
            {/* Welcome Card */}
            <div className="welcome-card">
              {/* Orange Top Bar */}
              <div className="welcome-card-orange-bar"></div>
              {/* Card Content */}
              <div className="welcome-card-content">
              <span className="welcome-card-emoji">👋</span>
              <h2>Welcome back, {studentData.first_name}!</h2>
              <p className="welcome-message">Here's your student information and activities.</p>
              </div>
            </div>

            {/* All Information Cards in One Row - Desktop */}
            <div className="all-info-cards-container">
              {/* Personal Information Card */}
              <div className="personal-info-card-full">
                <div className="info-card-modern">
                  <div className="card-header-modern">
                    <div className="card-icon-circle">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <h3 className="card-title-modern">Personal Information</h3>
                  </div>
                  <div className="card-body-modern">
                    <div className="info-item-modern">
                      <div className="info-label-modern">Name</div>
                      <div className="info-value-modern">{studentData.first_name} {studentData.middle_name ? `${studentData.middle_name} ` : ''}{studentData.last_name}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Email</div>
                      <div className="info-value-modern">
                        <a href={`mailto:${studentData.email}`} className="email-link-modern">{studentData.email}</a>
                      </div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Student ID</div>
                      <div className="info-value-modern">{studentData.student_id_no || 'N/A'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Nationality</div>
                      <div className="info-value-modern">{studentData.nationality || 'N/A'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Phone</div>
                      <div className="info-value-modern">{studentData.phone || 'N/A'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Date of Birth</div>
                      <div className="info-value-modern">{studentData.date_of_birth ? new Date(studentData.date_of_birth).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Age</div>
                      <div className="info-value-modern">{studentData.date_of_birth ? (() => { const d=new Date(studentData.date_of_birth); const t=new Date(); let a=t.getFullYear()-d.getFullYear(); const m=t.getMonth()-d.getMonth(); if(m<0 || (m===0 && t.getDate()<d.getDate())) a--; return a; })() : 'N/A'} years</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Class</div>
                      <div className="info-value-modern">{studentData.classes?.class_name || 'No Class'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Section</div>
                      <div className="info-value-modern">{studentData.section_id || 'N/A'}</div>
                    </div>
                    <div className="info-item-modern">
                      <div className="info-label-modern">Previous School</div>
                      <div className="info-value-modern">{studentData.previous_school || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Cards Grid - School and Parent */}
              <div className="info-cards-grid">
              {/* School Information Card */}
              <div className="info-card-modern">
                <div className="card-header-modern">
                  <div className="card-icon-circle">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <h3 className="card-title-modern">School Information</h3>
                </div>
                <div className="card-body-modern">
                  <div className="info-item-modern">
                    <div className="info-label-modern">School</div>
                    <div className="info-value-modern">{studentData.schools?.school_name || 'Unknown School'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Admission Date</div>
                    <div className="info-value-modern">{studentData.admission_date ? new Date(studentData.admission_date).toLocaleDateString() : 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Status</div>
                    <div className="info-value-modern">
                      <span className={`status-badge-modern ${studentData.is_active ? 'active' : 'inactive'}`}>
                        {studentData.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parent Information Card */}
              <div className="info-card-modern">
                <div className="card-header-modern">
                  <div className="card-icon-circle">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <h3 className="card-title-modern">Parent Information</h3>
                </div>
                <div className="card-body-modern">
                  <div className="info-item-modern">
                    <div className="info-label-modern">Name</div>
                    <div className="info-value-modern">{studentData.parent_name || 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Relationship</div>
                    <div className="info-value-modern">{studentData.parent_relationship || 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Contact Priority</div>
                    <div className="info-value-modern">{studentData.parent_is_primary === true ? 'Primary' : studentData.parent_is_primary === false ? 'Secondary' : 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Email</div>
                    <div className="info-value-modern">
                      {studentData.parent_email ? (
                        <a href={`mailto:${studentData.parent_email}`} className="email-link-modern">{studentData.parent_email}</a>
                      ) : 'N/A'}
                    </div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Phone</div>
                    <div className="info-value-modern">{studentData.parent_phone || 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Address</div>
                    <div className="info-value-modern">{studentData.parent_address || studentData.address || 'N/A'}</div>
                  </div>
                  <div className="info-item-modern">
                    <div className="info-label-modern">Permissions</div>
                    <div className="info-value-modern">{[
                      studentData.parent_can_pickup ? 'Can pick from school' : null,
                      studentData.parent_is_emergency_contact ? 'Emergency contact' : null,
                      studentData.parent_pays_fees ? 'Pays fees' : null
                    ].filter(Boolean).join(', ') || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button className="action-btn">
                  📚 View Assignments
                </button>
                <button className="action-btn">
                  📊 View Grades
                </button>
                <button className="action-btn" onClick={() => handleTabChange('schedule')}>
                  📅 View Schedule
                </button>
                <button className="action-btn">
                  💬 Contact Teacher
                </button>
              </div>
            </div>

            <div className="notices-section">
              <h3>📢 School Notices</h3>
              {noticesLoading ? (
                <div className="notices-loading">
                  <div className="loading-spinner">⏳</div>
                  <p>Loading notices...</p>
                </div>
              ) : notices.length === 0 ? (
                <div className="no-notices">
                  <div className="no-notices-icon">📢</div>
                  <p>No notices available at the moment.</p>
                  <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                    School ID: {studentData?.school_id || 'Not found'}
                  </p>
                </div>
              ) : (
                <div className="notices-list">
                  {notices.map((notice) => (
                    <div key={notice.id} className={`notice-card ${getPriorityClass(notice.priority)}`}>
                      <div className="notice-header">
                        <div className="notice-priority">
                          <span className="priority-icon">{getPriorityIcon(notice.priority)}</span>
                          <span className="priority-label">{getPriorityLabel(notice.priority)}</span>
                        </div>
                        <div className="notice-date">
                          📅 {formatDate(notice.created_at)}
                        </div>
                      </div>
                      <div className="notice-content">
                        <h4 className="notice-title">{notice.title}</h4>
                        <p className="notice-text">{notice.content}</p>
                        {notice.expiry_date && (
                          <div className="notice-expiry">
                            ⏰ Expires: {formatDate(notice.expiry_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'schedule' && (
          <div className="schedule-section-modern">
            {/* Page Title Section */}
            <div className="schedule-title-card">
              <div className="schedule-icon-large">📅</div>
              <h2 className="schedule-title">Class Schedule</h2>
              <p className="schedule-subtitle">Your weekly class schedule for {studentData.classes?.class_name} - {studentData.section_id}</p>
            </div>

            {scheduleLoading || timingsLoading ? (
              <div className="schedule-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading schedule...</p>
              </div>
            ) : Object.keys(schedulesBySemester).length > 0 ? (
              <>
                {/* School Timings Card */}
                {schoolTimings && (
                  <div className="timings-card-modern">
                    <div className="timings-header">
                      <span className="timings-icon">🕐</span>
                      <h3 className="timings-title">School Timings</h3>
                    </div>
                    <div className="timings-info-grid">
                      <div className="timing-info-item">
                        <div className="timing-label-modern">School Hours</div>
                        <div className="timing-value-modern">
                          {(() => {
                            const startTime = schoolTimings.start_time || '08:00';
                            const endTime = schoolTimings.end_time || '15:00';
                            return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                          })()}
                        </div>
                      </div>
                      <div className="timing-info-item">
                        <div className="timing-label-modern">Period Duration</div>
                        <div className="timing-value-modern">{schoolTimings.period_time || 45} minutes</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Semester Schedules */}
                <div className="semester-schedules-container">
                  {Object.keys(schedulesBySemester).map((semester, index) => {
                    const semesterData = schedulesBySemester[semester];
                    const semesterSchedule = semesterData.schedule;
                    
                    // Get working days and periods for this semester's schedule
                    const getWorkingDaysForSemester = () => {
                      const days = new Set();
                      Object.keys(semesterSchedule).forEach(key => {
                        const [day] = key.split('_');
                        days.add(day);
                      });
                      const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      return dayOrder.filter(day => days.has(day));
                    };
                    
                    const getPeriodsForSemester = () => {
                      const periods = new Set();
                      Object.keys(semesterSchedule).forEach(key => {
                        const [day, period] = key.split('_');
                        periods.add(parseInt(period));
                      });
                      return Array.from(periods).sort((a, b) => a - b);
                    };
                    
                    const workingDays = getWorkingDaysForSemester();
                    const periods = getPeriodsForSemester();
                    
                    return (
                      <div key={semester} className={`semester-schedule-section ${index === 0 ? 'first-semester' : ''}`}>
                        <div className="semester-header">
                          <h3 className="semester-title">
                            📚 {semesterData.semester_name || semester}
                            {index === 0 && <span className="primary-badge">Primary</span>}
                          </h3>
                        </div>
                        <div className="schedule-table-container">
                          <table className="schedule-table">
                            <thead>
                              <tr className="schedule-table-header">
                                <th className="period-header">Period</th>
                                {workingDays.map(day => (
                                  <th key={day} className="day-header">
                                    {getDayName(day)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const rows = [];
                                
                                periods.forEach(period => {
                                  // Add period row
                                  const timing = schoolTimings ? calculatePeriodTimingsForPeriod(period) : null;
                                  
                                  rows.push(
                                    <tr key={`period_${period}`} className="schedule-table-row">
                                      <td className="period-cell">
                                        <div className="period-label">Period {period}</div>
                                        {timing ? (
                                          <div className="period-timing">
                                            <div className="timing-text">{timing.start} - {timing.end}</div>
                                          </div>
                                        ) : (
                                          <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                            No school timings set
                                          </div>
                                        )}
                                      </td>
                                      {workingDays.map(day => {
                                        const key = `${day}_${period}`;
                                        const scheduleItem = semesterSchedule[key] || {};
                                        
                                        return (
                                          <td key={day} className="schedule-cell">
                                            {scheduleItem.subject_name ? (
                                              <div className="schedule-assignment-display">
                                                <div className="subject-display">
                                                  <strong>{scheduleItem.subject_name}</strong>
                                                </div>
                                                {scheduleItem.teacher_name && (
                                                  <div className="teacher-display">
                                                    <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="empty-slot">
                                                <span className="empty-text">Free Period</span>
                                              </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                  
                                  // Add break row if there's a break after this period
                                  if (timing && timing.breakAfter) {
                                    rows.push(
                                      <tr key={`break_after_${period}`} className="break-table-row">
                                        <td className="break-period-cell">
                                          <div className="break-label">☕ Break</div>
                                          <div className="break-timing">
                                            {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                          </div>
                                        </td>
                                        {workingDays.map(day => (
                                          <td key={day} className="break-cell">
                                            <div className="break-display">
                                              <span className="break-text">Break Time</span>
                                            </div>
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  }
                                });
                                
                                return rows;
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : scheduleData && scheduleData.schedule ? (
              <>
                {/* School Timings Card */}
                {schoolTimings && (
                  <div className="timings-card-modern">
                    <div className="timings-header">
                      <span className="timings-icon">🕐</span>
                      <h3 className="timings-title">School Timings</h3>
                    </div>
                    <div className="timings-info-grid">
                      <div className="timing-info-item">
                        <div className="timing-label-modern">School Hours</div>
                        <div className="timing-value-modern">
                          {(() => {
                            const startTime = schoolTimings.start_time || '08:00';
                            const endTime = schoolTimings.end_time || '15:00';
                            return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                          })()}
                        </div>
                      </div>
                      <div className="timing-info-item">
                        <div className="timing-label-modern">Period Duration</div>
                        <div className="timing-value-modern">{schoolTimings.period_time || 45} minutes</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Schedule Table */}
                <div className="schedule-table-container">
                  <table className="schedule-table">
                    <thead>
                      <tr className="schedule-table-header">
                        <th className="period-header">Period</th>
                        {getWorkingDays().map(day => (
                          <th key={day} className="day-header">
                            {getDayName(day)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const periods = getPeriods();
                        const rows = [];
                        
                        periods.forEach(period => {
                          // Add period row
                          const timing = schoolTimings ? calculatePeriodTimingsForPeriod(period) : null;
                          
                          rows.push(
                            <tr key={`period_${period}`} className="schedule-table-row">
                              <td className="period-cell">
                                <div className="period-label">Period {period}</div>
                                {timing ? (
                                  <div className="period-timing">
                                    <div className="timing-text">{timing.start} - {timing.end}</div>
                                  </div>
                                ) : (
                                  <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                    No school timings set
                                  </div>
                                )}
                              </td>
                              {getWorkingDays().map(day => {
                                const key = `${day}_${period}`;
                                const scheduleItem = scheduleData.schedule[key] || {};
                                
                                return (
                                  <td key={day} className="schedule-cell">
                                    {scheduleItem.subject_name ? (
                                      <div className="schedule-assignment-display">
                                        <div className="subject-display">
                                          <strong>{scheduleItem.subject_name}</strong>
                                        </div>
                                        {scheduleItem.teacher_name && (
                                          <div className="teacher-display">
                                            <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="empty-slot">
                                        <span className="empty-text">Free Period</span>
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          
                          // Add break row if there's a break after this period
                          if (timing && timing.breakAfter) {
                            rows.push(
                              <tr key={`break_after_${period}`} className="break-table-row">
                                <td className="break-period-cell">
                                  <div className="break-label">☕ Break</div>
                                  <div className="break-timing">
                                    {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                  </div>
                                </td>
                                {getWorkingDays().map(day => (
                                  <td key={day} className="break-cell">
                                    <div className="break-display">
                                      <span className="break-text">Break Time</span>
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            );
                          }
                        });
                        
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="no-schedule">
                <div className="no-schedule-icon">📅</div>
                <h3>No Schedule Available</h3>
                <p>Your class schedule has not been set up yet by the school administrator.</p>
                <p>Please contact your teacher or school office for more information.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="fees-section">
            {/* Page Header */}
            <div className="fees-page-header">
              <span className="fees-header-icon">💰</span>
              <h1 className="fees-page-title">Fee Details</h1>
              <p className="fees-page-subtitle">Fees due for {studentData.classes?.class_name || 'N/A'} - {studentData.section_id || 'N/A'}</p>
            </div>

            {feesLoading ? (
              <div className="fees-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading fee information...</p>
              </div>
            ) : studentFees.length === 0 ? (
              <div className="no-fees">
                <div className="no-fees-icon">💰</div>
                <h3>No Fees Due</h3>
                <p>You don't have any fees due at the moment.</p>
                <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                  Check back later or contact the school office for more information.
                </p>
              </div>
            ) : (
              <div className="fee-container">
                {studentFees.map((fee, index) => {
                  // Parse month and year from fee.month (e.g., "December 2025")
                  const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
                  const monthName = monthParts[0] || '';
                  const year = monthParts[1] || new Date().getFullYear().toString();
                  
                  return (
                    <div key={index} className="fee-card-nexy">
                      {/* Left Sidebar */}
                      <div className="fee-sidebar">
                        <div className="fee-month-display">
                          <h3 className="fee-month-name">{monthName}</h3>
                          <div className="fee-year">{year}</div>
                        </div>
                        <div className="fee-type-badge-nexy">MONTHLY</div>
                        <div className={`fee-status-badge-nexy ${fee.isPaid ? 'fee-status-paid' : 'fee-status-due'}`}>
                          {fee.isPaid ? 'PAID' : 'DUE NOW'}
                        </div>
                        <div className="fee-sidebar-arrow"></div>
                      </div>
                      
                      {/* Right Content Section */}
                      <div className="fee-content-nexy">
                        {/* Amount Display Card */}
                        <div className="fee-amount-card">
                          <div className="fee-currency-label">{fee.currency || 'PKR'}</div>
                          <div className="fee-amount-value">{fee.feeAmount?.toLocaleString() || '0'}</div>
                        </div>
                        
                        {/* Details Section */}
                        <div className="fee-details-nexy">
                          <div className="fee-detail-row">
                            <span className="fee-detail-label">Due:</span>
                            <span className="fee-detail-value">
                              <span className="fee-calendar-icon">📅</span>
                              {fee.dueDate ? fee.dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="attendance-section">
            {/* Page Header Card */}
            <div className="attendance-header-card">
              <div className="attendance-header-content">
                <div className="attendance-icon-title">
                  <h2 className="attendance-header-title">My Attendance</h2>
                </div>
                <p className="attendance-header-subtitle">
                  Your attendance records for {studentData.classes?.class_name || 'GRADE 6'} - Section {studentData.section_id || 'A'}
                </p>
              </div>
            </div>

            {/* Month Navigation */}
            <div className="month-navigation">
              <button 
                className="nav-btn nav-btn-prev"
                onClick={() => navigateMonth('prev')}
              >
                <span className="nav-arrow">←</span>
                <span className="nav-text">Previous</span>
              </button>
              <div className="current-month">
                <h3>{getMonthName(selectedMonth)} {selectedYear}</h3>
              </div>
              <button 
                className="nav-btn nav-btn-next"
                onClick={() => navigateMonth('next')}
              >
                <span className="nav-text">Next</span>
                <span className="nav-arrow">→</span>
              </button>
            </div>

            {/* Loading State */}
            {attendanceLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading attendance records...</p>
              </div>
            ) : attendanceRecords.length === 0 ? (
              /* Empty State */
              <div className="attendance-empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 19V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V19M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19M9 19H5C3.89543 19 3 18.1046 3 17V7C3 5.89543 3.89543 5 5 5H9M15 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 className="empty-state-title">No Attendance Records</h3>
                <p className="empty-state-description">
                  No attendance records found for {getMonthName(selectedMonth)} {selectedYear}
                </p>
                <p className="empty-state-helper">
                  Records will appear here once your teacher starts marking attendance
                </p>
              </div>
            ) : (
              <>
                {/* Statistics Cards */}
                <div className="attendance-stats-grid">
                  <div className="stat-card stat-card-total">
                  <div className="stat-number">{getAttendanceStats().totalRecords}</div>
                  <div className="stat-label">Total Days</div>
                </div>
                  <div className="stat-card stat-card-present">
                  <div className="stat-number">{getAttendanceStats().presentCount}</div>
                  <div className="stat-label">Present</div>
                </div>
                  <div className="stat-card stat-card-absent">
                  <div className="stat-number">{getAttendanceStats().absentCount}</div>
                  <div className="stat-label">Absent</div>
                </div>
                  <div className="stat-card stat-card-percentage">
                  <div className="stat-number">{getAttendanceStats().attendancePercentage}%</div>
                    <div className="stat-label">Attendance %</div>
                </div>
              </div>

                {/* Attendance Cards List */}
                <div className="attendance-cards-container">
                  <div className="attendance-cards-grid">
                    {attendanceRecords.map((record) => {
                      const date = new Date(record.attendance_date);
                      const formattedDate = date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      });
                      const teacherName = record.teacher 
                        ? `${record.teacher.first_name || ''} ${record.teacher.last_name || ''}`.trim()
                        : 'N/A';

                      return (
                        <div 
                          key={`${record.student_id}-${record.attendance_date}`} 
                          className="attendance-record-card"
                          style={{
                            background: '#FFFFFF',
                            borderRadius: '12px',
                            padding: '20px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            borderLeft: `4px solid ${
                              record.status === 'present' ? '#10B981' :
                              record.status === 'absent' ? '#EF4444' : '#F59E0B'
                            }`
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                              {formattedDate}
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 600,
                              background: record.status === 'present' ? '#D1FAE5' :
                                record.status === 'absent' ? '#FEE2E2' : '#FEF3C7',
                              color: record.status === 'present' ? '#065F46' :
                                record.status === 'absent' ? '#991B1B' : '#92400E'
                            }}>
                              {record.status.toUpperCase()}
                            </span>
                          </div>
                          {record.teacher && (
                            <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                              <strong>Teacher:</strong> {teacherName}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'homework' && (
          <div className="homework-section">
            {/* Page Header */}
            <div className="homework-header">
              <div className="homework-header-content">
                <div className="homework-header-title">
                  <span className="homework-header-icon">📝</span>
                  <h2>My Homework</h2>
                </div>
                <p className="homework-subtitle">Homework assignments for {studentData.classes?.class_name} - Section {studentData.section_id}</p>
              </div>
            </div>

            {/* Date Navigation */}
            <div className="homework-date-navigation">
              <button 
                className="nav-btn prev-btn"
                onClick={() => navigateHomeworkDate('prev')}
                disabled={homeworkLoading}
              >
                ← Previous Day
              </button>
              <div className="current-date">
                <h3>{new Date(homeworkDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</h3>
                <p className="date-info">
                  {homeworkDate === new Date().toISOString().split('T')[0] ? 'Today' : 
                   homeworkDate < new Date().toISOString().split('T')[0] ? 'Previous Day' : 'Future Day'}
                </p>
              </div>
              <button 
                className="nav-btn next-btn"
                onClick={() => navigateHomeworkDate('next')}
                disabled={homeworkLoading}
              >
                Next Day →
              </button>
            </div>

            {homeworkLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading homework...</p>
              </div>
            ) : homeworkData.length === 0 ? (
              <div className="no-homework">
                <div className="no-homework-icon">📚</div>
                <h3>No homework for this day</h3>
                <p>Check other dates or come back later</p>
              </div>
            ) : filteredHomework.length === 0 ? (
              <div className="no-homework">
                <div className="no-homework-icon">📅</div>
                <h3>No homework for this day</h3>
                <p>Check other dates or come back later</p>
              </div>
            ) : (
              <div className="homework-grid">
                {filteredHomework.map(homework => {
                  const completion = homeworkCompletionStatus[homework.id];
                  const isCompleted = completion?.is_completed || false;
                  
                  return (
                    <div key={homework.id} className={`homework-card ${isCompleted ? 'completed-homework' : ''}`}>
                      <div className="homework-header-card">
                        <div className="homework-title-section">
                          <h4>{homework.title}</h4>
                          {isCompleted && (
                            <span className="completion-badge completed-badge">
                              ✅ Completed
                            </span>
                          )}
                          {!isCompleted && completion && (
                            <span className="completion-badge pending-badge">
                              ⏳ Not Completed
                            </span>
                          )}
                        </div>
                        <span className="homework-subject">
                          <span className="subject-icon">📖</span>
                          {homework.subject}
                        </span>
                      </div>
                      
                      <div className="homework-details">
                        <div className="homework-description">
                          <p>{homework.description}</p>
                        </div>
                        
                        {isCompleted && completion.completed_at && (
                          <div className="completion-info">
                            <span className="completion-date">
                              ✓ Completed on: {new Date(completion.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          </div>
                        )}
                        
                        <div className="homework-dates">
                          <div className="assigned-date">
                            <span className="date-icon">📅</span>
                            <span>Assigned: {new Date(homework.assigned_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          </div>
                          <div className="due-date">
                            <span className="date-icon">⏰</span>
                            <span>Due: {new Date(homework.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'exam-schedule' && (
          <div className="exam-schedule-section">
            {/* Page Header */}
            <div className="exam-schedule-header">
              <div className="exam-schedule-header-content">
                <div className="exam-schedule-header-icon">📅</div>
                <div className="exam-schedule-header-title">
                  <h2>Tests & Exams</h2>
                </div>
                <p className="exam-schedule-subtitle">Upcoming exams, tests, and holidays for {studentData.classes?.class_name} - Section {studentData.section_id}</p>
              </div>
            </div>

            {examScheduleLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading exam schedule...</p>
              </div>
            ) : examScheduleData.length === 0 ? (
              <div className="no-exams">
                <div className="no-exams-icon">📅</div>
                <h3>No upcoming exams scheduled</h3>
              </div>
            ) : (
              <div className="exam-schedule-grid">
                {examScheduleData.map(exam => (
                  <div key={exam.id} className="exam-schedule-card">
                    <div className="exam-card-header">
                      <div className="exam-type-badge">
                        {exam.type === 'monthly_test' ? 'MONTHLY TEST' : 
                         exam.type === 'exam' ? 'EXAM' : 
                         exam.type === 'holiday' ? 'HOLIDAY' : 'TEST'}
                      </div>
                    </div>
                    
                    <div className="exam-card-content" style={{ textAlign: 'center' }}>
                      <h4 className="exam-title" style={{ textAlign: 'center', margin: '0 auto', width: '100%', display: 'block' }}>{exam.title}</h4>
                      
                      <div className="exam-dates">
                        <span className="exam-date">
                          {exam.start_date === exam.end_date 
                            ? new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : `${new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(exam.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                          }
                        </span>
                      </div>

                      {exam.subjects && exam.subjects.length > 0 && (
                        <div className="exam-subjects">
                          <div className="subjects-header">
                            <span className="subjects-title">Subjects ({exam.subjects.length})</span>
                          </div>
                          <div className="subjects-list">
                            {exam.subjects.map((subject, index) => (
                              <div key={index} className="subject-item">
                                <span className="subject-name">{subject.name}</span>
                                <div className="subject-date-time-row">
                                  <span className="subject-date">
                                    <span className="date-icon">📅</span>
                                    {new Date(subject.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </span>
                                  <span className="subject-time">
                                    <span className="time-icon">🕐</span>
                                    {subject.time}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="results-section">
            <div className="results-page-header">
              <div className="results-header-icon">📊</div>
              <h1 className="results-page-title">Exam Results</h1>
              <p className="results-page-subtitle">View your published exam and test results</p>
            </div>

            {resultsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading results...</p>
              </div>
            ) : examResults.length === 0 || (examResults[0] && examResults[0].examGroups.length === 0) ? (
              <div className="no-results">
                <div className="no-results-icon">📊</div>
                <h3>No Results Available</h3>
                <p>Exam results will appear here once they are published by the school.</p>
              </div>
            ) : (
              <div className="results-list">
                {examResults.map((studentResult, studentIdx) => (
                  <div key={studentIdx}>
                    {studentResult.examGroups.map((examGroup, examIdx) => (
                      <div key={examIdx} className="result-card">
                        <div className="result-card-header">
                          <h3 className="result-exam-name">{examGroup.exam?.title || 'Unknown Exam'}</h3>
                          <div className="result-exam-meta">
                            <span className="result-exam-type-badge">
                              {examGroup.exam?.type === 'exam' ? 'Exam' : 
                               examGroup.exam?.type === 'monthly_test' ? 'Monthly Test' : 
                               examGroup.exam?.type === 'holiday' ? 'Holiday' : 'Test'}
                            </span>
                            {examGroup.exam?.start_date && (
                              <span className="result-exam-date">
                                {new Date(examGroup.exam.start_date).toLocaleDateString('en-GB', { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="result-card-body">
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Subject</th>
                                <th>Obtained Marks</th>
                                <th>Total Marks</th>
                                <th>Percentage</th>
                                <th>Grade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {examGroup.marks.map((mark, markIdx) => (
                                <tr key={markIdx}>
                                  <td className="subject-cell">
                                    <strong>{mark.subject_name}</strong>
                                  </td>
                                  <td className="marks-cell">
                                    {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                                      ? parseFloat(mark.obtained_marks).toFixed(2)
                                      : '-'}
                                  </td>
                                  <td className="marks-cell">
                                    {mark.total_marks !== null && mark.total_marks !== undefined 
                                      ? parseFloat(mark.total_marks).toFixed(2)
                                      : '-'}
                                  </td>
                                  <td className="percentage-cell">
                                    {mark.percentage !== null && mark.percentage !== undefined 
                                      ? `${parseFloat(mark.percentage).toFixed(2)}%`
                                      : '-'}
                                  </td>
                                  <td className="grade-cell">
                                    {mark.grade ? (
                                      <span className={`grade-badge grade-${mark.grade.toUpperCase().charAt(0)}`}>
                                        {mark.grade}
                                      </span>
                                    ) : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td>
                                  <strong>
                                    {examGroup.marks.reduce((sum, m) => {
                                      const obtained = parseFloat(m.obtained_marks) || 0;
                                      return sum + obtained;
                                    }, 0).toFixed(2)}
                                  </strong>
                                </td>
                                <td>
                                  <strong>
                                    {examGroup.marks.reduce((sum, m) => {
                                      const total = parseFloat(m.total_marks) || 0;
                                      return sum + total;
                                    }, 0).toFixed(2)}
                                  </strong>
                                </td>
                                <td>
                                  <strong>
                                    {(() => {
                                      const totalObtained = examGroup.marks.reduce((sum, m) => {
                                        return sum + (parseFloat(m.obtained_marks) || 0);
                                      }, 0);
                                      const totalMarks = examGroup.marks.reduce((sum, m) => {
                                        return sum + (parseFloat(m.total_marks) || 0);
                                      }, 0);
                                      return totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(2) : '0.00';
                                    })()}%
                                  </strong>
                                </td>
                                <td colSpan="2"></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="result-card-action">
                          <button
                            onClick={() => {
                              setSelectedResultCard({
                                student: studentResult.student,
                                exam: examGroup.exam,
                                marks: examGroup.marks
                              });
                              setShowResultCard(true);
                            }}
                            className="view-result-card-btn-main"
                            title="View Result Card"
                          >
                            View Result Card
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {activeTab === 'chat' && (
          <div className="chat-section-modern">
            {/* Page Header */}
            <div className="chat-page-header">
              <div className="chat-header-icon">💬</div>
              <h1 className="chat-page-title">Chat</h1>
              <div className="chat-view-toggle">
                <button
                  className={`toggle-btn ${chatViewMode === 'teachers' ? 'active' : ''}`}
                  onClick={() => {
                    // Clean up group chat channel
                    if (window.currentGroupChatChannel) {
                      supabase.removeChannel(window.currentGroupChatChannel);
                      window.currentGroupChatChannel = null;
                    }
                    setChatViewMode('teachers');
                    setSelectedChatGroup(null);
                    setSelectedTeacher(null);
                  }}
                >
                  Teachers
                </button>
                <button
                  className={`toggle-btn ${chatViewMode === 'groups' ? 'active' : ''}`}
                  onClick={() => {
                    // Clean up individual chat channel
                    if (window.currentChatChannel) {
                      supabase.removeChannel(window.currentChatChannel);
                      window.currentChatChannel = null;
                    }
                    setChatViewMode('groups');
                    setSelectedTeacher(null);
                    setSelectedChatGroup(null);
                  }}
                >
                  Groups
                </button>
              </div>
            </div>

            {chatViewMode === 'teachers' ? (
              <>
                {teachersLoading ? (
                  <div className="chat-loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading teachers...</p>
                  </div>
                ) : teachers.length === 0 ? (
                  <div className="chat-no-teachers">
                    <div className="no-teachers-icon">👨‍🏫</div>
                    <h3>No Teachers Found</h3>
                    <p>No teachers are currently assigned to your class and section.</p>
                  </div>
                ) : (
                  <div className="chat-main-container">
                    {/* Left Sidebar - Teachers List */}
                    <div className={`chat-sidebar ${!showSidebar ? 'hidden-mobile' : ''}`}>
                      <div className="chat-sidebar-header">
                        <h3 className="chat-sidebar-title">Your Teachers</h3>
                      </div>
                  <div className="chat-search-container">
                    <input
                      type="text"
                      className="chat-search-input"
                      placeholder="Search teachers..."
                      value={teacherSearchQuery}
                      onChange={(e) => setTeacherSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="chat-teachers-list">
                    {teachers
                      .filter(teacher => {
                        if (!teacherSearchQuery.trim()) return true;
                        const query = teacherSearchQuery.toLowerCase();
                        const name = `${teacher.first_name} ${teacher.last_name}`.toLowerCase();
                        const subjects = (teacher.subjects || []).join(' ').toLowerCase();
                        return name.includes(query) || subjects.includes(query);
                      })
                      .map(teacher => (
                        <div
                          key={teacher.id}
                          className={`chat-teacher-card ${selectedTeacher?.id === teacher.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedTeacher(teacher);
                            setShowSidebar(false);
                          }}
                        >
                          <div className="chat-teacher-avatar-wrapper">
                            <div className="chat-teacher-avatar">{teacher.isAdmin ? '🏫' : '👨‍🏫'}</div>
                            <div className="chat-online-indicator"></div>
                          </div>
                          <div className="chat-teacher-info">
                            <h4 className="chat-teacher-name">{teacher.first_name} {teacher.last_name}</h4>
                            {teacher.subjects && teacher.subjects.length > 0 && (
                              <p className="chat-teacher-subject">{teacher.subjects.map(s => s.toUpperCase()).join(', ')}</p>
                            )}
                          </div>
                          {unreadCounts[teacher.id] > 0 && (
                            <div className="chat-unread-badge">{unreadCounts[teacher.id] > 99 ? '99+' : unreadCounts[teacher.id]}</div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Right Chat Area */}
                {selectedTeacher ? (
                  <div className="chat-window-modern">
                    <div className="chat-window-header-modern">
                      <button 
                        className="chat-back-button"
                        onClick={() => {
                          setShowSidebar(true);
                          setSelectedTeacher(null);
                        }}
                      >
                        ←
                      </button>
                      <div className="chat-header-avatar-wrapper">
                        <div className="chat-header-avatar">{selectedTeacher.isAdmin ? '🏫' : '👨‍🏫'}</div>
                        <div className="chat-online-indicator-small"></div>
                      </div>
                      <div className="chat-header-teacher-info">
                        <h3 className="chat-header-teacher-name">{selectedTeacher.first_name} {selectedTeacher.last_name}</h3>
                        {selectedTeacher.subjects && selectedTeacher.subjects.length > 0 && (
                          <p className="chat-header-teacher-subject">{selectedTeacher.subjects.map(s => s.toUpperCase()).join(', ')}</p>
                        )}
                      </div>
                    </div>

                    {chatLoading ? (
                      <div className="chat-loading-state">
                        <div className="loading-spinner">⏳</div>
                        <p>Loading chat...</p>
                      </div>
                    ) : (
                      <>
                        <div className="chat-messages-area">
                          {chatMessages.length === 0 ? (
                            <div className="chat-no-messages">
                              <p>No messages yet. Start the conversation!</p>
                            </div>
                          ) : (
                            <>
                              {chatMessages.map(message => {
                                // Check if message is unread
                                const lastReadKey = selectedTeacher.isAdmin 
                                  ? `lastRead_${studentData.id}_admin`
                                  : `lastRead_${studentData.id}_${selectedTeacher.id}`;
                                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                                const isUnread = (message.sender_type === 'teacher' || message.sender_type === 'school') && 
                                  (!lastReadTimestamp || message.created_at > lastReadTimestamp);
                                
                                // Mark message as read when viewed
                                if (isUnread && (message.sender_type === 'teacher' || message.sender_type === 'school')) {
                                  markMessageAsRead(message.id, message);
                                }

                                const messageStatus = getMessageStatus(message);
                                
                                return (
                                  <div
                                    key={message.id}
                                    className={`chat-message ${message.sender_type === 'student' && message.sender_id === studentData.id.toString() ? 'sent' : 'received'} ${isUnread ? 'has-unread-indicator' : ''}`}
                                  >
                                    <div className="chat-message-content">
                                      {isUnread && <div className="unread-indicator-circle"></div>}
                                      <div className="chat-message-sender">
                                        {message.sender_type === 'student' ? 'You' : selectedTeacher.isAdmin ? 'School Admin' : selectedTeacher.first_name}
                                      </div>
                                      {message.content && <div className="chat-message-text">{message.content}</div>}
                                      {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                        <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {message.media_urls.map((media, idx) => (
                                            <div key={idx}>
                                              {media.type && media.type.startsWith('image/') ? (
                                                <img
                                                  src={media.url}
                                                  alt={media.name || 'Image'}
                                                  style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                                  onClick={() => window.open(media.url, '_blank')}
                                                />
                                              ) : (
                                                <a
                                                  href={media.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    background: '#F3F4F6',
                                                    borderRadius: '8px',
                                                    textDecoration: 'none',
                                                    color: '#1F2937',
                                                    maxWidth: '300px'
                                                  }}
                                                >
                                                  <span>📄</span>
                                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {media.name || 'Document'}
                                                  </span>
                                                </a>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <div className="chat-message-time">
                                          {new Date(message.created_at).toLocaleString()}
                                        </div>
                                        {messageStatus && (
                                          <span style={{ 
                                            color: messageStatus.color, 
                                            fontSize: '14px', 
                                            marginLeft: '8px',
                                            fontWeight: '500'
                                          }} title={messageStatus.status === 'read' ? 'Read' : 'Delivered'}>
                                            {messageStatus.icon}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              <div ref={chatMessagesEndRef} />
                            </>
                          )}
                        </div>

                        <div className="chat-input-area-modern">
                          {selectedFiles.length > 0 && (
                            <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {selectedFiles.map((file, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                                  <span>{file.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                              id="chat-file-input-student-teacher"
                            />
                            <label
                              htmlFor="chat-file-input-student-teacher"
                              style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                              title="Attach file"
                            >
                              📎
                            </label>
                            <input
                              type="text"
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                              placeholder="Type your message..."
                              className="chat-input-modern"
                              style={{ flex: 1, minWidth: 0, width: '100%' }}
                            />
                            <button
                              onClick={sendMessage}
                              disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                              className="chat-send-button"
                              title="Send message"
                            >
                              {uploadingFiles ? '⏳' : '➤'}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="chat-empty-state">
                    <div className="chat-empty-icon">💬</div>
                    <h3 className="chat-empty-title">Select a Teacher</h3>
                    <p className="chat-empty-message">Choose a teacher from the list to start chatting</p>
                  </div>
                )}
                  </div>
                )}
              </>
            ) : (
              <>
                {loadingGroups ? (
                  <div className="chat-loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading chat groups...</p>
                  </div>
                ) : chatGroups.length === 0 ? (
                  <div className="chat-no-teachers">
                    <div className="no-teachers-icon">👥</div>
                    <h3>No Chat Groups</h3>
                    <p>You are not a member of any chat groups yet.</p>
                  </div>
                ) : (
                  <div className="chat-main-container">
                    {/* Left Sidebar - Groups List */}
                    <div className={`chat-sidebar ${!showSidebar ? 'hidden-mobile' : ''}`}>
                      <div className="chat-sidebar-header">
                        <h3 className="chat-sidebar-title">Chat Groups</h3>
                      </div>
                      <div className="chat-search-container">
                        <input
                          type="text"
                          className="chat-search-input"
                          placeholder="Search groups..."
                          value={teacherSearchQuery}
                          onChange={(e) => setTeacherSearchQuery(e.target.value)}
                        />
                      </div>
                      <div className="chat-teachers-list">
                        {chatGroups
                          .filter(group => {
                            if (!teacherSearchQuery.trim()) return true;
                            const query = teacherSearchQuery.toLowerCase();
                            return group.name.toLowerCase().includes(query) ||
                                   (group.description && group.description.toLowerCase().includes(query));
                          })
                          .map(group => (
                            <div
                              key={group.id}
                              className={`chat-teacher-card ${selectedChatGroup?.id === group.id ? 'selected' : ''}`}
                              onClick={() => {
                                loadGroupChat(group);
                                setShowSidebar(false);
                              }}
                            >
                              <div className="chat-teacher-avatar-wrapper">
                                <div className="chat-teacher-avatar">👥</div>
                                <div className="chat-online-indicator"></div>
                              </div>
                              <div className="chat-teacher-info">
                                <h4 className="chat-teacher-name">{group.name}</h4>
                                {group.lastMessage && (
                                  <p className="chat-teacher-subject">
                                    {group.lastMessageSender?.type === 'student' && group.lastMessageSender.id === studentData.id.toString() 
                                      ? 'You: ' 
                                      : group.lastMessageSender?.type === 'teacher' 
                                        ? 'Teacher: ' 
                                        : ''}
                                    {group.lastMessage.length > 30 ? group.lastMessage.substring(0, 30) + '...' : group.lastMessage}
                                  </p>
                                )}
                                {group.memberCount > 0 && (
                                  <p className="chat-teacher-subject" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Right Chat Area - Group Chat */}
                    {selectedChatGroup ? (
                      <div className="chat-window-modern">
                        <div className="chat-window-header-modern">
                          <button 
                            className="chat-back-button"
                            onClick={() => {
                              // Clean up group chat channel
                              if (window.currentGroupChatChannel) {
                                supabase.removeChannel(window.currentGroupChatChannel);
                                window.currentGroupChatChannel = null;
                              }
                              setShowSidebar(true);
                              setSelectedChatGroup(null);
                            }}
                          >
                            ←
                          </button>
                          <div className="chat-header-avatar-wrapper">
                            <div className="chat-header-avatar">👥</div>
                            <div className="chat-online-indicator-small"></div>
                          </div>
                          <div className="chat-header-teacher-info">
                            <h3 className="chat-header-teacher-name">{selectedChatGroup.name}</h3>
                            {selectedChatGroup.description && (
                              <p className="chat-header-teacher-subject">{selectedChatGroup.description}</p>
                            )}
                            {selectedChatGroup.memberCount > 0 && (
                              <p className="chat-header-teacher-subject" style={{ fontSize: '0.75rem' }}>
                                {selectedChatGroup.memberCount} member{selectedChatGroup.memberCount !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        {groupChatLoading ? (
                          <div className="chat-loading-state">
                            <div className="loading-spinner">⏳</div>
                            <p>Loading chat...</p>
                          </div>
                        ) : (
                          <>
                            <div className="chat-messages-area">
                              {groupChatMessages.length === 0 ? (
                                <div className="chat-no-messages">
                                  <p>No messages yet. Start the conversation!</p>
                                </div>
                              ) : (
                                <>
                                  {groupChatMessages.map(message => {
                                    const isOwnMessage = message.sender_type === 'student' && message.sender_id === studentData.id.toString();
                                    
                                    // Mark message as read when viewed (for received messages)
                                    if (!isOwnMessage) {
                                      markMessageAsRead(message.id, message);
                                    }

                                    // Get read count for group messages
                                    const readBy = message.read_by || [];
                                    const readCount = readBy.length;
                                    const totalMembers = selectedChatGroup?.memberCount || 1;
                                    const messageStatus = isOwnMessage ? {
                                      status: readCount > 0 ? 'read' : 'delivered',
                                      icon: readCount > 0 ? '✓✓' : '✓✓',
                                      color: readCount > 0 ? '#4F46E5' : '#9CA3AF',
                                      readCount: readCount,
                                      totalMembers: totalMembers
                                    } : null;
                                    
                                    return (
                                      <div
                                        key={message.id}
                                        className={`chat-message ${isOwnMessage ? 'sent' : 'received'}`}
                                      >
                                        <div className="chat-message-content">
                                          <div className="chat-message-sender">
                                            {message.senderName || 'Unknown'}
                                          </div>
                                          {message.content && <div className="chat-message-text">{message.content}</div>}
                                          {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                            <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                              {message.media_urls.map((media, idx) => (
                                                <div key={idx}>
                                                  {media.type && media.type.startsWith('image/') ? (
                                                    <img
                                                      src={media.url}
                                                      alt={media.name || 'Image'}
                                                      style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                                      onClick={() => window.open(media.url, '_blank')}
                                                    />
                                                  ) : (
                                                    <a
                                                      href={media.url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px 12px',
                                                        background: '#F3F4F6',
                                                        borderRadius: '8px',
                                                        textDecoration: 'none',
                                                        color: '#1F2937',
                                                        maxWidth: '300px'
                                                      }}
                                                    >
                                                      <span>📄</span>
                                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {media.name || 'Document'}
                                                      </span>
                                                    </a>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                                            <div className="chat-message-time">
                                              {new Date(message.created_at).toLocaleString()}
                                            </div>
                                            {messageStatus && (
                                              <span style={{ 
                                                color: messageStatus.color, 
                                                fontSize: '14px', 
                                                marginLeft: '8px',
                                                fontWeight: '500'
                                              }} title={messageStatus.readCount > 0 ? `Read by ${messageStatus.readCount}/${messageStatus.totalMembers}` : 'Delivered'}>
                                                {messageStatus.icon}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <div ref={chatMessagesEndRef} />
                                </>
                              )}
                            </div>

                            <div className="chat-input-area-modern">
                              {selectedFiles.length > 0 && (
                                <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                  {selectedFiles.map((file, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                                      <span>{file.name}</span>
                                      <button
                                        type="button"
                                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  multiple
                                  accept="image/*,.pdf,.doc,.docx,.txt"
                                  onChange={handleFileSelect}
                                  style={{ display: 'none' }}
                                  id="chat-file-input-student"
                                />
                                <label
                                  htmlFor="chat-file-input-student"
                                  style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                                  title="Attach file"
                                >
                                  📎
                                </label>
                                <input
                                  type="text"
                                  value={newMessage}
                                  onChange={(e) => setNewMessage(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      sendGroupMessage(e);
                                    }
                                  }}
                                  placeholder="Type your message..."
                                  className="chat-input-modern"
                                  style={{ flex: 1, minWidth: 0, width: '100%' }}
                                />
                                <button
                                  type="button"
                                  onClick={(e) => sendGroupMessage(e)}
                                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                                  className="chat-send-button"
                                  title="Send message"
                                >
                                  {uploadingFiles ? '⏳' : '➤'}
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="chat-empty-state">
                        <div className="chat-empty-icon">👥</div>
                        <h3 className="chat-empty-title">Select a Group</h3>
                        <p className="chat-empty-message">Choose a chat group from the list to start chatting</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Result Card Modal */}
      {showResultCard && selectedResultCard && (
        <div className="modal-overlay" onClick={() => setShowResultCard(false)}>
          <div className="result-card-modal" onClick={(e) => e.stopPropagation()}>
            <div className="result-card-header">
              <div className="result-card-school-info">
                <h1>REPORT CARD</h1>
                <p className="school-name">{selectedResultCard.student?.school_name || 'School Name'}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowResultCard(false)}>
                ✕
              </button>
            </div>

            <div className="result-card-body">
              {/* Student Info Section */}
              <div className="result-card-student-info">
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Student Name:</span>
                    <span className="info-value">
                      {selectedResultCard.student?.first_name} {selectedResultCard.student?.middle_name || ''} {selectedResultCard.student?.last_name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Student ID:</span>
                    <span className="info-value">{selectedResultCard.student?.student_id_no || selectedResultCard.student?.id || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Class:</span>
                    <span className="info-value">{selectedResultCard.student?.classes?.class_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Section:</span>
                    <span className="info-value">{selectedResultCard.student?.section_id || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Exam/Test:</span>
                    <span className="info-value">{selectedResultCard.exam?.title || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Type:</span>
                    <span className="info-value">
                      {selectedResultCard.exam?.type === 'exam' ? 'Exam' : 
                       selectedResultCard.exam?.type === 'monthly_test' ? 'Monthly Test' : 
                       selectedResultCard.exam?.type === 'holiday' ? 'Holiday' : 'Test'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Marks Table */}
              <div className="result-card-marks-table">
                <table className="result-card-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Obtained Marks</th>
                      <th>Total Marks</th>
                      <th>Percentage</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResultCard.marks.map((mark, idx) => (
                      <tr key={idx}>
                        <td className="subject-cell">
                          <strong>{mark.subject_name}</strong>
                        </td>
                        <td className="marks-cell">
                          {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                            ? parseFloat(mark.obtained_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="marks-cell">
                          {mark.total_marks !== null && mark.total_marks !== undefined 
                            ? parseFloat(mark.total_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="percentage-cell">
                          {mark.percentage !== null && mark.percentage !== undefined 
                            ? `${parseFloat(mark.percentage).toFixed(2)}%`
                            : '-'}
                        </td>
                        <td className="grade-cell">
                          {mark.grade ? (
                            <span className={`grade-badge-large grade-${mark.grade.toUpperCase().charAt(0)}`}>
                              {mark.grade}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>
                        <strong>
                          {selectedResultCard.marks.reduce((sum, m) => {
                            const obtained = parseFloat(m.obtained_marks) || 0;
                            return sum + obtained;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {selectedResultCard.marks.reduce((sum, m) => {
                            const total = parseFloat(m.total_marks) || 0;
                            return sum + total;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {(() => {
                            const totalObtained = selectedResultCard.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.obtained_marks) || 0);
                            }, 0);
                            const totalMarks = selectedResultCard.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.total_marks) || 0);
                            }, 0);
                            return totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(2) : '0.00';
                          })()}%
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Remarks Section */}
              {selectedResultCard.marks.some(m => m.remarks) && (
                <div className="result-card-remarks">
                  <h3>Remarks</h3>
                  <div className="remarks-list">
                    {selectedResultCard.marks.filter(m => m.remarks).map((mark, idx) => (
                      <div key={idx} className="remark-item">
                        <strong>{mark.subject_name}:</strong> {mark.remarks}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

        {activeTab === 'transport' && (
          <div className="transport-section">
            {transportLoading ? (
              <div className="transport-loading">
                <div className="loading-spinner"></div>
                <p>Loading transport information...</p>
              </div>
            ) : !studentData?.transport_service_enabled ? (
              <div className="transport-not-enabled">
                <div className="transport-icon">🚌</div>
                <h2>Transport Service Not Enabled</h2>
                <p>Transport service is not enabled for your account. Please contact your school administration if you need transport services.</p>
              </div>
            ) : !transportData ? (
              <div className="transport-no-route">
                <div className="transport-icon">🚌</div>
                <h2>No Route Assigned</h2>
                <p>You don't have a transport route assigned yet. Please contact your school administration.</p>
              </div>
            ) : (
              <div className="transport-content">
                {/* Transport Header */}
                <div className="transport-header">
                  <div className="transport-header-icon">🚌</div>
                  <div className="transport-header-info">
                    <h1>Bus Tracking</h1>
                    <p>Track your assigned bus in real-time</p>
                  </div>
                </div>

                {/* Transport Details Card */}
                <div className="transport-details-card">
                  <h3>Route Information</h3>
                  <div className="transport-info-grid">
                    <div className="transport-info-item">
                      <span className="transport-info-label">Route Name:</span>
                      <span className="transport-info-value">
                        {transportData.route_name || `Route #${transportData.id}`}
                      </span>
                    </div>
                    <div className="transport-info-item">
                      <span className="transport-info-label">Route Type:</span>
                      <span className="transport-info-value">{transportData.route_type}</span>
                    </div>
                    {transportData.vehicle && (
                      <>
                        <div className="transport-info-item">
                          <span className="transport-info-label">Bus Number:</span>
                          <span className="transport-info-value">{transportData.vehicle.bus_number}</span>
                        </div>
                        {transportData.vehicle.driver_name && (
                          <div className="transport-info-item">
                            <span className="transport-info-label">Driver:</span>
                            <span className="transport-info-value">{transportData.vehicle.driver_name}</span>
                          </div>
                        )}
                        {transportData.seatNumber && (
                          <div className="transport-info-item">
                            <span className="transport-info-label">Your Seat Number:</span>
                            <span className="transport-info-value" style={{ 
                              fontSize: '18px', 
                              fontWeight: '700', 
                              color: '#1F2937' 
                            }}>
                              Seat {transportData.seatNumber}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {transportData.pickupLocation && (
                      <div className="transport-info-item">
                        <span className="transport-info-label">Your Pickup Location:</span>
                        <span className="transport-info-value">
                          {transportData.pickupLocation.location_name}
                          {transportData.pickupLocation.address && ` - ${transportData.pickupLocation.address}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Live Bus Location Map */}
                {transportData.allPickupLocations && transportData.allPickupLocations.length > 0 && (
                  <div className="transport-map-container">
                    <h3>Live Bus Location</h3>
                    <BusLocationMap 
                      busLocation={busLocation}
                      pickupLocations={transportData.allPickupLocations}
                      studentPickupLocation={transportData.pickupLocation}
                    />
                    {busLocation ? (
                      <div className="bus-status">
                        <span className="bus-status-indicator active"></span>
                        <span>Bus is on route - Last updated: {new Date(busLocation.timestamp).toLocaleTimeString()}</span>
                      </div>
                    ) : (
                      <div className="bus-status">
                        <span className="bus-status-indicator inactive"></span>
                        <span>Bus location not available</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>
      )}

        {activeTab === 'meals' && (
          <div className="meals-section">
            {/* Page Header */}
            <div className="meals-page-header">
              <span className="meals-header-icon">🍽️</span>
              <h1 className="meals-page-title">School Meals</h1>
              <p className="meals-page-subtitle">View meals available at your school</p>
            </div>

            {mealsLoading ? (
              <div className="meals-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading meals...</p>
              </div>
            ) : meals.length === 0 ? (
              <div className="no-meals">
                <div className="no-meals-icon">🍽️</div>
                <h3>No Meals Available</h3>
                <p>No meals have been added by the school yet.</p>
                <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                  Check back later or contact the school office for more information.
                </p>
              </div>
            ) : (
              <div className="meals-grid">
                {meals.map(meal => (
                  <div key={meal.id} className="meal-card">
                    <div className="meal-card-header">
                      <div className="meal-type-badge">
                        {meal.meal_type === 'Break' ? '☕' : meal.meal_type === 'Lunch' ? '🍽️' : '🍪'}
                        <span className="meal-type-text">{meal.meal_type}</span>
                      </div>
                      <div className={`meal-frequency-badge ${meal.frequency.toLowerCase()}`}>
                        {meal.frequency}
                      </div>
                    </div>
                    <div className="meal-card-content">
                      <h3 className="meal-name">{meal.meal_name}</h3>
                      <p className="meal-description">{meal.description}</p>
                      <div className="meal-footer">
                        <span className="meal-date">
                          📅 Added: {new Date(meal.created_at).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
    </div>
  );
}

// Bus Location Map Component
function BusLocationMap({ busLocation, pickupLocations, studentPickupLocation }) {
  const validLocations = pickupLocations.filter(loc => loc.latitude && loc.longitude);
  
  // Show map if we have bus location OR valid pickup locations
  if (validLocations.length === 0 && !busLocation) {
    return (
      <div className="map-placeholder">
        <p>Map coordinates not available for this route</p>
      </div>
    );
  }

  // Calculate center
  let centerLat, centerLng;
  if (busLocation) {
    // If we have bus location, center on it
    centerLat = busLocation.lat;
    centerLng = busLocation.lng;
    // If we also have pickup locations, average them with bus location
    if (validLocations.length > 0) {
      const allPoints = [...validLocations, busLocation];
      centerLat = allPoints.reduce((sum, loc) => sum + (loc.lat || parseFloat(loc.latitude)), 0) / allPoints.length;
      centerLng = allPoints.reduce((sum, loc) => sum + (loc.lng || parseFloat(loc.longitude)), 0) / allPoints.length;
    }
  } else if (validLocations.length > 0) {
    centerLat = validLocations.reduce((sum, loc) => sum + parseFloat(loc.latitude), 0) / validLocations.length;
    centerLng = validLocations.reduce((sum, loc) => sum + parseFloat(loc.longitude), 0) / validLocations.length;
  } else {
    // Fallback center (shouldn't reach here due to check above)
    centerLat = 33.5651; // Default to Rawalpindi
    centerLng = 73.0169;
  }

  // Create route polyline
  const routeCoords = validLocations.map(loc => [
    parseFloat(loc.latitude),
    parseFloat(loc.longitude)
  ]);

  return (
    <MapContainer
      center={[centerLat, centerLng]}
      zoom={13}
      style={{ height: '400px', width: '100%', borderRadius: '12px', zIndex: 1 }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {/* Bus Location Marker */}
      {busLocation && (
        <Marker
          position={[busLocation.lat, busLocation.lng]}
          icon={getBusIcon()}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <strong>🚌 Your Bus</strong>
              <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                Live Location
              </div>
            </div>
          </Popup>
        </Marker>
      )}
      
      {/* Pickup Location Markers */}
      {validLocations.map((location, index) => {
        const isStudentLocation = studentPickupLocation && location.id === studentPickupLocation.id;
        return (
          <Marker
            key={location.id}
            position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
            icon={isStudentLocation ? getDefaultIcon() : undefined}
          >
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <strong>{location.location_name}</strong>
                {location.address && (
                  <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                    {location.address}
                  </div>
                )}
                {isStudentLocation && (
                  <div style={{ fontSize: '11px', marginTop: '4px', color: '#FF8B3D', fontWeight: '600' }}>
                    🎯 Your Pickup Point
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
      
      {/* Route Polyline */}
      {routeCoords.length > 1 && (
        <Polyline
          positions={routeCoords}
          color="#FF8B3D"
          weight={4}
          opacity={0.7}
        />
      )}
    </MapContainer>
  );
}

export default StudentDashboard;